import { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import MoneyPinGate, { getMoneyStamp } from './MoneyPinGate';
import { ChevronLeft, ChevronRight, Plus, Trash2, Upload, Image as ImageIcon, X, Edit3, Check, Users, ListChecks, Sparkles, Loader2, Calendar as CalIcon, Sunrise, CalendarDays, CalendarRange, Wand2, Printer, MessageSquare, Send, GripVertical, CalendarPlus, RefreshCw } from 'lucide-react';
import jsPDF from 'jspdf';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import RycomMeetings from './RycomMeetings';

const API = process.env.REACT_APP_BACKEND_URL;

const ParseSummary = ({ info, onClose }) => (
  <div className="border-b border-border px-4 py-3 font-sans text-xs space-y-1.5" data-testid="schedule-parse-summary">
    <div className="flex items-start justify-between gap-3">
      <span className="text-emerald-400" data-testid="schedule-parse-matched">
        Read {info.employees.length} employee{info.employees.length === 1 ? '' : 's'}: {info.employees.map(e => `${e.name}${e.name_in_image && e.name_in_image !== e.name ? ` (“${e.name_in_image}”)` : ''} · ${e.shifts.length} shifts`).join(' — ')}
      </span>
      <button type="button" onClick={onClose} data-testid="schedule-parse-summary-close" className="text-neutral-500 hover:text-white">✕</button>
    </div>
    {info.unmatched?.length > 0 && (
      <div className="text-amber-300" data-testid="schedule-parse-unmatched">
        Not on your employee list, so skipped: {info.unmatched.join(', ')}. Add them under Employees and re-parse if they should get tasks.
      </div>
    )}
    {info.warnings?.map(w => <div key={w} className="text-brand" data-testid="schedule-parse-warning">⚠ {w}</div>)}
  </div>
);

const monthKey = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
const monthLabel = (d) => d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
const daysInMonth = (year, mon) => new Date(year, mon, 0).getDate();
const DOW = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

/** Mini revenue strip shown in the daily focus modal. PIN-gated; shows nothing if not unlocked. */
function DailyRevenueStrip({ month, day }) {
  const [loaded, setLoaded] = useState(false);
  const [data, setData] = useState(null);
  const [hasStamp, setHasStamp] = useState(!!getMoneyStamp());
  const [showGate, setShowGate] = useState(false);

  useEffect(() => {
    setHasStamp(!!getMoneyStamp());
    setData(null);
    setLoaded(false);
  }, [month, day]);

  const fetchIt = async () => {
    const stamp = getMoneyStamp();
    if (!stamp) { setShowGate(true); return; }
    setLoaded(true);
    try {
      const dateStr = `${month}-${String(day).padStart(2, '0')}`;
      const { data: d } = await axios.get(`${API}/api/admin/square/daily-revenue?date=${dateStr}`, {
        withCredentials: true,
        headers: { 'X-Money-Stamp': stamp },
      });
      setData(d);
    } catch (_) {
      setData({ error: true });
    }
  };

  if (showGate) {
    return (
      <div className="mt-3" data-testid="daily-revenue-strip">
        <MoneyPinGate title="Reveal Day Revenue">
          <button
            onClick={() => { setShowGate(false); fetchIt(); }}
            className="font-heading text-xs tracking-widest uppercase bg-brand text-white px-4 py-2 rounded-lg hover:bg-brand-hover transition-colors"
          >
            Show Day Revenue
          </button>
        </MoneyPinGate>
      </div>
    );
  }

  if (!hasStamp || !loaded) {
    return (
      <button
        onClick={fetchIt}
        data-testid="daily-revenue-btn"
        className="mt-3 inline-flex items-center gap-1.5 text-[10px] font-heading tracking-widest uppercase border border-dashed border-yellow-700/50 text-yellow-500/80 hover:text-yellow-400 hover:border-yellow-500 px-3 py-1.5 rounded-lg transition-colors"
      >
        💰 Show day revenue
      </button>
    );
  }

  return (
    <div className="mt-3 flex items-center gap-3 rounded-lg px-3 py-2 bg-gradient-to-r from-yellow-900/20 via-green-900/10 to-transparent border border-yellow-700/30" data-testid="daily-revenue-loaded">
      {data && !data.error ? (
        <>
          <span className="font-heading text-[10px] tracking-widest uppercase text-yellow-500">Day Revenue</span>
          <span className="font-heading text-xl text-green-400">{data.formatted}</span>
          <span className="font-sans text-[10px] text-muted-foreground">{data.count} payment{data.count !== 1 ? 's' : ''}</span>
        </>
      ) : (
        <span className="font-sans text-[10px] text-red-400">Could not load — check Square account in Settings</span>
      )}
    </div>
  );
}


const FREQ_META = {
  daily:   { label: 'Daily',   icon: Sunrise,       desc: 'Every day an employee works — one person per day (round-robin).' },
  weekly:  { label: 'Weekly',  icon: CalendarDays,  desc: 'Once per week — rotates across staff each week.' },
  monthly: { label: 'Monthly', icon: CalendarRange, desc: 'Once this month — distributed across employees (the "Kiara on laundry" use-case).' },
};

export default function TeamTasks() {
  const [cursor, setCursor] = useState(new Date());
  const [archive, setArchive] = useState([]); // [{month, shifts, tasks, has_image}]
  const [employees, setEmployees] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [walkins, setWalkins] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [routineItems, setRoutineItems] = useState([]);
  const [closingRoutineItems, setClosingRoutineItems] = useState([]);
  const [routineCompletions, setRoutineCompletions] = useState([]);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatDraft, setChatDraft] = useState('');
  const [chatAuthor, setChatAuthor] = useState('');
  const [supplies, setSupplies] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [supplyDraft, setSupplyDraft] = useState('');
  const [supplyAuthor, setSupplyAuthor] = useState('');
  const [schedule, setSchedule] = useState(null);
  const [library, setLibrary] = useState([]);
  const [editEmp, setEditEmp] = useState(null);
  const [newEmployee, setNewEmployee] = useState('');
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [showSchedule, setShowSchedule] = useState(true);
  const [parseError, setParseError] = useState('');
  const [parseInfo, setParseInfo] = useState(null);
  const [notice, setNotice] = useState('');
  const [view, setView] = useState('calendar'); // 'calendar' | 'tasks'
  const [focus, setFocus] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [exportError, setExportError] = useState('');
  const fileInputRef = useRef(null);
  const calendarRef = useRef(null);

  const month = monthKey(cursor);
  const [y, m] = month.split('-').map(Number);
  const totalDays = daysInMonth(y, m);
  const firstDow = new Date(y, m - 1, 1).getDay();

  const fetchAll = async () => {
    setLoading(true);
    try {
      // Auto roll-over any uncompleted weekly/monthly tasks from past days to later shifts.
      // Fires before the GETs so results reflect the rolled-forward state.
      try {
        await axios.post(`${API}/api/team/tasks/rollover`, { month }, { withCredentials: true });
      } catch (_) { /* non-fatal */ }
      const [eRes, tRes, sRes, shRes, libRes, aRes, wRes, rmRes, rvRes, rtRes, rtCloseRes, chatRes, supRes, meetRes] = await Promise.all([
        axios.get(`${API}/api/team/employees`, { withCredentials: true }),
        axios.get(`${API}/api/team/tasks?month=${month}`, { withCredentials: true }),
        axios.get(`${API}/api/team/schedule?month=${month}`, { withCredentials: true }),
        axios.get(`${API}/api/team/shifts?month=${month}`, { withCredentials: true }),
        axios.get(`${API}/api/team/library`, { withCredentials: true }),
        axios.get(`${API}/api/team/months`, { withCredentials: true }),
        axios.get(`${API}/api/team/walkins?month=${month}`, { withCredentials: true }),
        axios.get(`${API}/api/team/rooms`, { withCredentials: true }),
        axios.get(`${API}/api/team/reservations?month=${month}`, { withCredentials: true }),
        axios.get(`${API}/api/team/routine?routine_type=open`, { withCredentials: true }),
        axios.get(`${API}/api/team/routine?routine_type=close`, { withCredentials: true }),
        axios.get(`${API}/api/team/chat?month=${month}`, { withCredentials: true }),
        axios.get(`${API}/api/team/supplies`, { withCredentials: true }),
        axios.get(`${API}/api/team/rycom-meetings?month=${month}`, { withCredentials: true }),
      ]);
      setEmployees(eRes.data);
      setTasks(tRes.data);
      setSchedule(sRes.data?.image_base64 ? sRes.data : null);
      setShifts(shRes.data);
      setLibrary(libRes.data);
      setArchive(aRes.data);
      setWalkins(wRes.data);
      setRooms(rmRes.data);
      setReservations(rvRes.data);
      setRoutineItems(rtRes.data);
      setClosingRoutineItems(rtCloseRes.data);
      setChatMessages(chatRes.data);
      setSupplies(supRes.data);
      setMeetings(meetRes.data);
    } catch (e) { /* auth interceptor handles */ }
    setLoading(false);
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, [month]);

  // Remember the "live" month at mount so we can auto-roll forward if a new month rolls over
  // while the admin tab is left open — but only if the user hasn't manually navigated elsewhere.
  const mountedLiveMonth = useRef(monthKey(new Date()));
  useEffect(() => {
    const rollIfNeeded = () => {
      const nowLive = monthKey(new Date());
      // Only auto-jump when (a) we've crossed into a new real-world month AND
      // (b) the cursor is still on what was previously "live" (i.e. the user didn't move it).
      if (nowLive !== mountedLiveMonth.current && monthKey(cursor) === mountedLiveMonth.current) {
        const now = new Date();
        setCursor(new Date(now.getFullYear(), now.getMonth(), 1));
        mountedLiveMonth.current = nowLive;
      }
    };
    const iv = setInterval(rollIfNeeded, 60000);
    return () => clearInterval(iv);
    // eslint-disable-next-line
  }, [cursor]);

  const prevMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1));
  const nextMonth = () => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1));

  // --- Task library CRUD ---
  const addLibraryTask = async (title, frequency) => {
    const t = (title || '').trim();
    if (!t) return;
    const { data } = await axios.post(`${API}/api/team/library`, { title: t, frequency, notes: '' }, { withCredentials: true });
    setLibrary(list => [...list, data]);
  };
  const updateLibraryTask = async (id, patch) => {
    const cur = library.find(x => x.id === id);
    const next = { ...cur, ...patch };
    const { data } = await axios.put(`${API}/api/team/library/${id}`, { title: next.title, frequency: next.frequency, notes: next.notes || '' }, { withCredentials: true });
    setLibrary(list => list.map(x => x.id === id ? data : x));
  };
  const removeLibraryTask = async (id) => {
    await axios.delete(`${API}/api/team/library/${id}`, { withCredentials: true });
    setLibrary(list => list.filter(x => x.id !== id));
  };

  // --- Auto-assign library → calendar ---
  const runAutoAssign = async () => {
    setAssigning(true);
    setNotice('');
    try {
      const { data } = await axios.post(`${API}/api/team/auto-assign`, { month, clear_existing: true }, { withCredentials: true });
      setNotice(`${data.created} tasks auto-assigned across the calendar.`);
      await fetchAll();
      setView('calendar');
    } catch (err) {
      setNotice('Failed: ' + (err.response?.data?.detail || err.message));
    }
    setAssigning(false);
  };

  // --- Sync missing library tasks → keeps existing completions, only adds new ones ---
  const runSyncLibrary = async () => {
    setAssigning(true);
    setNotice('');
    try {
      const { data } = await axios.post(`${API}/api/team/sync-library`, { month }, { withCredentials: true });
      if (data.created > 0) {
        setNotice(`${data.created} missing task${data.created === 1 ? '' : 's'} added to ${monthLabel(cursor)} — your existing completions were preserved.`);
        await fetchAll();
        setView('calendar');
      } else {
        setNotice('All library tasks are already on the calendar — nothing to sync.');
      }
    } catch (err) {
      setNotice('Failed: ' + (err.response?.data?.detail || err.message));
    }
    setAssigning(false);
  };

  const runRollover = async () => {
    setNotice('');
    try {
      const { data } = await axios.post(`${API}/api/team/tasks/rollover`, { month }, { withCredentials: true });
      setNotice(data.moved > 0
        ? `${data.moved} uncompleted task${data.moved === 1 ? '' : 's'} rolled forward to a later shift.`
        : 'No overdue tasks to roll over.');
      await fetchAll();
    } catch (err) {
      setNotice('Roll-over failed: ' + (err.response?.data?.detail || err.message));
    }
  };

  // --- Task instance CRUD (on calendar / focus panel) ---
  const toggleTask = async (t) => {
    const updated = { ...t, done: !t.done };
    await axios.put(`${API}/api/team/tasks/${t.id}`, updated, { withCredentials: true });
    setTasks(list => list.map(x => x.id === t.id ? updated : x));
  };
  const updateTaskTitle = async (t, title) => {
    const updated = { ...t, title };
    await axios.put(`${API}/api/team/tasks/${t.id}`, updated, { withCredentials: true });
    setTasks(list => list.map(x => x.id === t.id ? updated : x));
  };
  const removeTask = async (id) => {
    await axios.delete(`${API}/api/team/tasks/${id}`, { withCredentials: true });
    setTasks(list => list.filter(x => x.id !== id));
  };
  const pushTaskToNextShift = async (t) => {
    try {
      const { data } = await axios.post(`${API}/api/team/tasks/${t.id}/push-next-shift`, {}, { withCredentials: true });
      const moved = data.moved_to;
      if (moved.month === month) {
        setTasks(list => list.map(x => x.id === t.id ? { ...x, day: moved.day, done: false } : x));
        setNotice(`"${t.title}" moved to day ${moved.day}.`);
      } else {
        // Moved out of current month — drop from local list and inform admin
        setTasks(list => list.filter(x => x.id !== t.id));
        setNotice(`"${t.title}" moved to ${moved.month} day ${moved.day}.`);
      }
    } catch (err) {
      setNotice(err.response?.data?.detail || 'Could not move task.');
    }
  };
  const addTaskForEmployee = async (empId, title, day) => {
    const clean = (title || '').trim();
    if (!clean) return;
    const body = { employee_id: empId, month, title: clean, done: false, notes: '' };
    if (day) body.day = day;
    const { data } = await axios.post(`${API}/api/team/tasks`, body, { withCredentials: true });
    setTasks(list => [...list, data]);
    return data;
  };

  // --- Shifts CRUD (coverage swaps / overrides) ---
  const reassignShift = async (sid, newEmpId) => {
    const { data } = await axios.put(`${API}/api/team/shifts/${sid}`, { employee_id: newEmpId }, { withCredentials: true });
    setShifts(list => list.map(s => s.id === data.id ? data : s));
    // Refresh tasks — the backend may have migrated day-tasks to the new employee
    const tRes = await axios.get(`${API}/api/team/tasks?month=${month}`, { withCredentials: true });
    setTasks(tRes.data);
    return data;
  };
  const addShift = async (empId, day) => {
    const { data } = await axios.post(`${API}/api/team/shifts`, { employee_id: empId, month, day, start: '', end: '' }, { withCredentials: true });
    setShifts(list => [...list, data]);
    return data;
  };
  const deleteShift = async (sid) => {
    await axios.delete(`${API}/api/team/shifts/${sid}`, { withCredentials: true });
    setShifts(list => list.filter(s => s.id !== sid));
  };

  // --- Walk-ins CRUD ---
  const addWalkin = async (empId, day, shiftId, name, paid) => {
    const clean = (name || '').trim();
    if (!clean) return;
    const { data } = await axios.post(`${API}/api/team/walkins`, { employee_id: empId, month, day, shift_id: shiftId, name: clean, paid: !!paid }, { withCredentials: true });
    setWalkins(list => [...list, data]);
  };
  const toggleWalkinPaid = async (w) => {
    const { data } = await axios.put(`${API}/api/team/walkins/${w.id}`, { paid: !w.paid }, { withCredentials: true });
    setWalkins(list => list.map(x => x.id === w.id ? data : x));
  };
  const updateWalkinName = async (w, name) => {
    const clean = (name || '').trim();
    if (!clean) return;
    const { data } = await axios.put(`${API}/api/team/walkins/${w.id}`, { name: clean }, { withCredentials: true });
    setWalkins(list => list.map(x => x.id === w.id ? data : x));
  };
  const removeWalkin = async (id) => {
    await axios.delete(`${API}/api/team/walkins/${id}`, { withCredentials: true });
    setWalkins(list => list.filter(x => x.id !== id));
  };

  // --- Rooms & reservations ---
  const addRoom = async (name) => {
    const clean = (name || '').trim();
    if (!clean) return;
    const { data } = await axios.post(`${API}/api/team/rooms`, { name: clean, order: rooms.length }, { withCredentials: true });
    setRooms(list => [...list, data].sort((a, b) => a.order - b.order));
  };
  const removeRoom = async (id) => {
    await axios.delete(`${API}/api/team/rooms/${id}`, { withCredentials: true });
    setRooms(list => list.filter(r => r.id !== id));
    setReservations(list => list.filter(r => r.room_id !== id));
  };
  const setReservationCount = async (empId, day, shiftId, roomId, count) => {
    const { data } = await axios.post(`${API}/api/team/reservations`, {
      employee_id: empId, month, day, shift_id: shiftId, room_id: roomId, count,
    }, { withCredentials: true });
    setReservations(list => {
      const filtered = list.filter(r => !(r.room_id === roomId && r.shift_id === shiftId && r.day === day && r.employee_id === empId));
      if (data && data.deleted) return filtered;
      if (data && data.id) return [...filtered, data];
      return filtered;
    });
  };

  // --- Open Routine ---
  const addRoutineItem = async (title) => {
    const clean = (title || '').trim();
    if (!clean) return;
    const { data } = await axios.post(`${API}/api/team/routine`, { title: clean, routine_type: 'open' }, { withCredentials: true });
    setRoutineItems(list => [...list, data]);
  };
  const removeRoutineItem = async (id) => {
    await axios.delete(`${API}/api/team/routine/${id}`, { withCredentials: true });
    setRoutineItems(list => list.filter(r => r.id !== id));
    setRoutineCompletions(list => list.filter(c => c.routine_id !== id));
  };
  const reorderRoutineItem = async (oldIdx, newIdx) => {
    const next = arrayMove(routineItems, oldIdx, newIdx);
    setRoutineItems(next);
    await Promise.all(next.map((r, i) => axios.put(`${API}/api/team/routine/${r.id}`, { title: r.title, order: i }, { withCredentials: true })));
  };
  // --- Closing Routine ---
  const addClosingRoutineItem = async (title) => {
    const clean = (title || '').trim();
    if (!clean) return;
    const { data } = await axios.post(`${API}/api/team/routine`, { title: clean, routine_type: 'close' }, { withCredentials: true });
    setClosingRoutineItems(list => [...list, data]);
  };
  const removeClosingRoutineItem = async (id) => {
    await axios.delete(`${API}/api/team/routine/${id}`, { withCredentials: true });
    setClosingRoutineItems(list => list.filter(r => r.id !== id));
    setRoutineCompletions(list => list.filter(c => c.routine_id !== id));
  };
  const reorderClosingRoutineItem = async (oldIdx, newIdx) => {
    const next = arrayMove(closingRoutineItems, oldIdx, newIdx);
    setClosingRoutineItems(next);
    await Promise.all(next.map((r, i) => axios.put(`${API}/api/team/routine/${r.id}`, { title: r.title, order: i }, { withCredentials: true })));
  };
  const fetchRoutineCompletions = async (shiftId) => {
    const { data } = await axios.get(`${API}/api/team/routine/completions?shift_id=${shiftId}`, { withCredentials: true });
    setRoutineCompletions(data);
  };
  const toggleRoutineCompletion = async (routineId, shiftId, done) => {
    await axios.post(`${API}/api/team/routine/completions`, { routine_id: routineId, shift_id: shiftId, done }, { withCredentials: true });
    if (done) {
      setRoutineCompletions(list => [...list.filter(c => !(c.routine_id === routineId && c.shift_id === shiftId)), { routine_id: routineId, shift_id: shiftId, done: true }]);
    } else {
      setRoutineCompletions(list => list.filter(c => !(c.routine_id === routineId && c.shift_id === shiftId)));
    }
  };


  // --- Employees CRUD ---
  const addEmployee = async () => {
    const name = newEmployee.trim();
    if (!name) return;
    const palette = ['#ff2a9d', '#00e5ff', '#ffe15a', '#5aff8a', '#b96aff', '#ff6b6b', '#22d3ee'];
    const color = palette[employees.length % palette.length];
    const { data } = await axios.post(`${API}/api/team/employees`, { name, color }, { withCredentials: true });
    setEmployees(e => [...e, data]);
    setNewEmployee('');
  };
  const saveEmployeeEdit = async () => {
    if (!editEmp?.id) return;
    const { data } = await axios.put(`${API}/api/team/employees/${editEmp.id}`, { name: editEmp.name, color: editEmp.color }, { withCredentials: true });
    setEmployees(list => list.map(e => e.id === data.id ? data : e));
    setEditEmp(null);
  };
  const removeEmployee = async (id) => {
    await axios.delete(`${API}/api/team/employees/${id}`, { withCredentials: true });
    setEmployees(list => list.filter(e => e.id !== id));
    setTasks(list => list.filter(t => t.employee_id !== id));
    setShifts(list => list.filter(s => s.employee_id !== id));
  };

  // --- Schedule upload + AI parse ---
  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = reader.result.toString().split(',')[1];
      await axios.post(`${API}/api/team/schedule`, { month, image_base64: base64 }, { withCredentials: true });
      setSchedule({ month, image_base64: base64 });
      setShowSchedule(true);
      await runParse(base64);
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };
  const runParse = async (explicitB64) => {
    const b64 = explicitB64 || schedule?.image_base64;
    if (!b64) return;
    setParsing(true);
    setParseError('');
    setParseInfo(null);
    try {
      const { data } = await axios.post(`${API}/api/team/schedule/parse`, { month, image_base64: b64 }, { withCredentials: true });
      setParseInfo(data);
      await fetchAll();
    } catch (err) {
      setParseError(err.response?.data?.detail || 'Failed to parse schedule');
    } finally {
      setParsing(false);
    }
  };
  const removeSchedule = async () => {
    if (!window.confirm('Remove the uploaded schedule image for this month?')) return;
    await axios.delete(`${API}/api/team/schedule?month=${month}`, { withCredentials: true });
    setSchedule(null);
  };

  // --- PDF export of the calendar ---
  const downloadPdf = async () => {
    setExporting(true);
    setExportError('');
    try {
      // Build a fully vector PDF using jsPDF primitives (crisp text, small file, print-ready)
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'letter', compress: true });
      const pageW = pdf.internal.pageSize.getWidth();   // 792
      const pageH = pdf.internal.pageSize.getHeight();  // 612
      const margin = 28;

      // --- Background ---
      pdf.setFillColor(14, 14, 18);
      pdf.rect(0, 0, pageW, pageH, 'F');

      // --- Header ---
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.text('OKI-ESCAPE  ·  TEAM SCHEDULE', margin, margin + 6);
      pdf.setTextColor(230, 55, 63);
      pdf.setFontSize(14);
      pdf.text(monthLabel(cursor).toUpperCase(), pageW - margin, margin + 6, { align: 'right' });

      // --- Legend (employees) ---
      const legendY = margin + 22;
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      let lx = margin;
      employees.forEach(e => {
        const rgb = hexToRgb(e.color);
        pdf.setFillColor(rgb.r, rgb.g, rgb.b);
        pdf.circle(lx + 3, legendY - 2, 3, 'F');
        pdf.setTextColor(210, 210, 210);
        pdf.text(e.name, lx + 10, legendY);
        lx += pdf.getTextWidth(e.name) + 22;
        if (lx > pageW - margin - 80) return;
      });

      // --- Calendar grid geometry ---
      const gridTop = legendY + 14;
      const gridBottom = pageH - margin - 14;
      const gridH = gridBottom - gridTop;
      const colW = (pageW - margin * 2) / 7;
      const headerRowH = 14;
      const weeksCount = Math.ceil((firstDow + totalDays) / 7);
      const cellH = (gridH - headerRowH) / weeksCount;

      // --- Day-of-week header row ---
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(8);
      pdf.setTextColor(140, 140, 140);
      DOW.forEach((d, i) => {
        pdf.text(d, margin + colW * i + colW / 2, gridTop + 10, { align: 'center' });
      });

      // --- Draw each day cell ---
      for (let i = 0; i < weeksCount * 7; i++) {
        const dayNum = i - firstDow + 1;
        const col = i % 7;
        const row = Math.floor(i / 7);
        const x = margin + col * colW;
        const y = gridTop + headerRowH + row * cellH;

        // Cell background + border
        const isWknd = col === 0 || col === 6;
        pdf.setFillColor(isWknd ? 22 : 18, isWknd ? 12 : 18, isWknd ? 20 : 26);
        pdf.rect(x, y, colW, cellH, 'F');
        pdf.setDrawColor(40, 40, 50);
        pdf.setLineWidth(0.4);
        pdf.rect(x, y, colW, cellH, 'S');

        if (dayNum < 1 || dayNum > totalDays) continue;

        // Day number
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.setTextColor(isWknd ? 230 : 170, isWknd ? 55 : 170, isWknd ? 63 : 170);
        pdf.text(String(dayNum), x + 4, y + 10);

        // Collect shifts + tasks for this day
        const dayShifts = shifts.filter(s => s.day === dayNum);
        let cy = y + 16;
        const maxY = y + cellH - 4;

        dayShifts.forEach(s => {
          if (cy > maxY - 10) return;
          const emp = employees.find(e => e.id === s.employee_id);
          if (!emp) return;
          const rgb = hexToRgb(emp.color);
          const dayTasks = tasksFor(emp.id, dayNum);
          const doneT = dayTasks.filter(t => t.done).length;

          // Shift header row: name + count
          pdf.setFillColor(rgb.r, rgb.g, rgb.b);
          pdf.rect(x + 3, cy, 2, 8, 'F'); // color stripe
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(7);
          pdf.setTextColor(rgb.r, rgb.g, rgb.b);
          const firstName = emp.name.split(' ')[0];
          pdf.text(firstName.toUpperCase(), x + 8, cy + 6);

          if (dayTasks.length > 0) {
            pdf.setFillColor(rgb.r, rgb.g, rgb.b);
            const badgeTxt = `${doneT}/${dayTasks.length}`;
            const badgeW = pdf.getTextWidth(badgeTxt) + 4;
            pdf.roundedRect(x + colW - badgeW - 3, cy, badgeW, 8, 1, 1, 'F');
            pdf.setTextColor(0, 0, 0);
            pdf.setFontSize(6.5);
            pdf.text(badgeTxt, x + colW - badgeW / 2 - 3, cy + 6, { align: 'center' });
          }
          cy += 9;

          // Tasks or time
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(6.3);
          pdf.setTextColor(220, 220, 220);
          if (dayTasks.length === 0) {
            pdf.text(`${s.start}-${s.end}`, x + 6, cy + 4);
            cy += 8;
          } else {
            // Wrap each task to 2 lines max
            const innerW = colW - 10;
            dayTasks.slice(0, 4).forEach(t => {
              if (cy > maxY) return;
              const prefix = t.done ? '[x] ' : '• ';
              const lines = pdf.splitTextToSize(prefix + t.title, innerW);
              const shown = lines.slice(0, 2);
              shown.forEach(ln => {
                if (cy > maxY) return;
                if (t.done) pdf.setTextColor(130, 130, 130);
                else pdf.setTextColor(235, 235, 235);
                pdf.text(ln, x + 6, cy + 4);
                cy += 6.5;
              });
            });
            if (dayTasks.length > 4 && cy < maxY) {
              pdf.setTextColor(rgb.r, rgb.g, rgb.b);
              pdf.setFontSize(6);
              pdf.text(`+${dayTasks.length - 4} more`, x + 6, cy + 4);
              cy += 6;
            }
          }
          cy += 2;
        });
      }

      // --- Footer ---
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(140, 140, 140);
      const footer = `${employees.length} employees  ·  ${shifts.length} shifts  ·  ${tasks.length} tasks  ·  generated ${new Date().toLocaleString()}`;
      pdf.text(footer, pageW / 2, pageH - 10, { align: 'center' });

      // --- Page 2: per-employee task summary ---
      pdf.addPage();
      pdf.setFillColor(14, 14, 18);
      pdf.rect(0, 0, pageW, pageH, 'F');
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.text('TASK ASSIGNMENTS  ·  BY EMPLOYEE', margin, margin + 6);
      pdf.setTextColor(230, 55, 63);
      pdf.setFontSize(14);
      pdf.text(monthLabel(cursor).toUpperCase(), pageW - margin, margin + 6, { align: 'right' });

      const colCount = 2;
      const colGap = 18;
      const colWidth = (pageW - margin * 2 - colGap * (colCount - 1)) / colCount;
      let py = margin + 26;
      let pcol = 0;

      for (const emp of employees) {
        const empTasks = tasks.filter(t => t.employee_id === emp.id);
        const empWalkinCount = walkins.filter(w => w.employee_id === emp.id).length;
        const empResCount = reservations.filter(r => r.employee_id === emp.id).reduce((s, r) => s + (r.count || 0), 0);
        if (empTasks.length === 0 && empWalkinCount === 0 && empResCount === 0) continue;

        // Start new column if overflow (extra 26pt reserved for the totals block)
        if (py + 20 + 26 + empTasks.length * 10 > pageH - margin - 20) {
          if (pcol < colCount - 1) { pcol++; py = margin + 26; }
          else { pdf.addPage(); pdf.setFillColor(14, 14, 18); pdf.rect(0, 0, pageW, pageH, 'F'); py = margin + 10; pcol = 0; }
        }

        const px = margin + pcol * (colWidth + colGap);
        const rgb = hexToRgb(emp.color);

        // Employee header bar
        pdf.setFillColor(rgb.r, rgb.g, rgb.b);
        pdf.roundedRect(px, py, colWidth, 16, 2, 2, 'F');
        pdf.setTextColor(0, 0, 0);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.text(emp.name.toUpperCase(), px + 6, py + 11);
        pdf.setFontSize(8);
        pdf.text(`${empTasks.filter(t => t.done).length}/${empTasks.length} done`, px + colWidth - 6, py + 11, { align: 'right' });
        py += 22;

        // Monthly totals: walk-ins and reservations for this employee
        const empWalkins = walkins.filter(w => w.employee_id === emp.id);
        const walkPaid = empWalkins.filter(w => w.paid).length;
        const walkUnpaid = empWalkins.length - walkPaid;
        const empRes = reservations.filter(r => r.employee_id === emp.id);
        const resTotal = empRes.reduce((s, r) => s + (r.count || 0), 0);
        // Aggregate per room
        const perRoom = {};
        empRes.forEach(r => { perRoom[r.room_id] = (perRoom[r.room_id] || 0) + (r.count || 0); });
        const roomBreakdown = Object.entries(perRoom)
          .map(([rid, n]) => {
            const rm = rooms.find(x => x.id === rid);
            return rm ? `${rm.name} ×${n}` : null;
          })
          .filter(Boolean)
          .join(', ');

        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(7.5);
        pdf.setTextColor(rgb.r, rgb.g, rgb.b);
        pdf.text('MONTHLY TOTALS', px + 2, py + 4);
        py += 8;

        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8.5);
        pdf.setTextColor(235, 235, 235);
        const walkLine = `Walk-ins: ${empWalkins.length} total (${walkPaid} paid${walkUnpaid ? `, ${walkUnpaid} unpaid` : ''})`;
        pdf.splitTextToSize(walkLine, colWidth - 6).forEach(ln => {
          pdf.text(ln, px + 6, py + 5); py += 9;
        });
        const resLine = `Reservations: ${resTotal} total` + (roomBreakdown ? ` — ${roomBreakdown}` : '');
        pdf.splitTextToSize(resLine, colWidth - 6).forEach(ln => {
          pdf.text(ln, px + 6, py + 5); py += 9;
        });
        py += 4;

        // Weekly & Monthly tasks only
        const grouped = { weekly: [], monthly: [] };
        empTasks.forEach(t => { if (t.frequency === 'weekly' || t.frequency === 'monthly') grouped[t.frequency].push(t); });

        for (const freq of ['weekly', 'monthly']) {
          const list = grouped[freq];
          if (!list.length) continue;
          pdf.setFont('helvetica', 'bold');
          pdf.setFontSize(7.5);
          pdf.setTextColor(rgb.r, rgb.g, rgb.b);
          pdf.text(freq.toUpperCase(), px + 2, py + 4);
          py += 8;

          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8.5);
          list.forEach(t => {
            if (py > pageH - margin - 10) return;
            pdf.setTextColor(t.done ? 130 : 235, t.done ? 130 : 235, t.done ? 130 : 235);
            const prefix = t.done ? '[x] ' : '[ ] ';
            const dayTag = t.day ? ` (day ${t.day})` : '';
            const lines = pdf.splitTextToSize(prefix + t.title + dayTag, colWidth - 6);
            lines.slice(0, 2).forEach(ln => {
              pdf.text(ln, px + 6, py + 5);
              py += 9;
            });
          });
          py += 4;
        }
        py += 10;
      }

      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(140, 140, 140);
      pdf.text(footer, pageW / 2, pageH - 10, { align: 'center' });

      // ===== PAGE 2: Summary Stats, Activity Log, Supplies =====
      pdf.addPage();
      pdf.setFillColor(14, 14, 18);
      pdf.rect(0, 0, pageW, pageH, 'F');

      // Header
      pdf.setTextColor(255, 255, 255);
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(16);
      pdf.text('OKI-ESCAPE  ·  MONTHLY SUMMARY', margin, margin + 6);
      pdf.setTextColor(230, 55, 63);
      pdf.setFontSize(14);
      pdf.text(monthLabel(cursor).toUpperCase(), pageW - margin, margin + 6, { align: 'right' });

      let sy = margin + 30;

      // --- Summary Stats ---
      const totalWalkins = walkins.length;
      const totalReservations = reservations.reduce((sum, r) => sum + (r.count || 0), 0);
      const stats = [
        { label: 'EMPLOYEES', val: employees.length },
        { label: 'SHIFTS', val: shifts.length },
        { label: 'WALK-INS', val: totalWalkins },
        { label: 'RESERVATIONS', val: totalReservations },
      ];
      const boxW = (pageW - margin * 2 - 18) / 4;
      stats.forEach((s, i) => {
        const bx = margin + i * (boxW + 6);
        pdf.setDrawColor(60, 60, 60);
        pdf.setFillColor(22, 22, 28);
        pdf.roundedRect(bx, sy, boxW, 36, 4, 4, 'FD');
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(18);
        pdf.setTextColor(255, 255, 255);
        pdf.text(String(s.val), bx + boxW / 2, sy + 16, { align: 'center' });
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(7);
        pdf.setTextColor(140, 140, 140);
        pdf.text(s.label, bx + boxW / 2, sy + 28, { align: 'center' });
      });
      sy += 50;

      // --- Activity Log ---
      pdf.setFont('helvetica', 'bold');
      pdf.setFontSize(10);
      pdf.setTextColor(230, 55, 63);
      pdf.text('ACTIVITY LOG', margin, sy);
      sy += 12;
      if (chatMessages.length === 0) {
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text('No notes this month.', margin + 4, sy);
        sy += 12;
      } else {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        chatMessages.slice(0, 30).forEach(msg => {
          if (sy > pageH - 40) return;
          const date = new Date(msg.created_at);
          const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          pdf.setTextColor(100, 100, 100);
          pdf.text(`${dateStr} · ${msg.author}`, margin + 4, sy);
          sy += 9;
          pdf.setTextColor(210, 210, 210);
          const lines = pdf.splitTextToSize(msg.message, pageW - margin * 2 - 10);
          lines.slice(0, 2).forEach(ln => {
            pdf.text(ln, margin + 8, sy);
            sy += 9;
          });
          sy += 3;
        });
      }
      sy += 8;

      // --- Supplies Needed ---
      if (sy < pageH - 60) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(10);
        pdf.setTextColor(200, 170, 50);
        pdf.text('SUPPLIES NEEDED', margin, sy);
        sy += 12;
        if (supplies.length === 0) {
          pdf.setFont('helvetica', 'italic');
          pdf.setFontSize(8);
          pdf.setTextColor(100, 100, 100);
          pdf.text('No supplies needed.', margin + 4, sy);
        } else {
          pdf.setFont('helvetica', 'normal');
          pdf.setFontSize(8);
          supplies.forEach(s => {
            if (sy > pageH - 30) return;
            const prefix = s.done ? '[x] ' : '[ ] ';
            pdf.setTextColor(s.done ? 100 : 210, s.done ? 100 : 210, s.done ? 100 : 210);
            const txt = prefix + s.item + (s.added_by ? ` — ${s.added_by}` : '');
            pdf.text(txt, margin + 4, sy);
            sy += 10;
          });
        }
      }

      // Footer page 2
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(8);
      pdf.setTextColor(140, 140, 140);
      pdf.text(footer, pageW / 2, pageH - 10, { align: 'center' });

      // Download via blob
      const blob = pdf.output('blob');
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `okiescape-schedule-${month}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      setNotice(`PDF downloaded: okiescape-schedule-${month}.pdf`);
    } catch (e) {
      console.error('PDF export failed', e);
      setExportError('PDF export failed: ' + (e.message || String(e)));
    } finally {
      setExporting(false);
    }
  };

  // Helper: hex -> rgb
  const hexToRgb = (hex) => {
    const h = hex.replace('#', '');
    return {
      r: parseInt(h.substring(0, 2), 16),
      g: parseInt(h.substring(2, 4), 16),
      b: parseInt(h.substring(4, 6), 16),
    };
  };

  // Earliest working day per employee in the current month — used to anchor legacy monthly
  // tasks that were inserted without a `day` (before the auto-assign fix).
  const firstShiftDay = (() => {
    const map = {};
    shifts.forEach(s => {
      if (map[s.employee_id] === undefined || s.day < map[s.employee_id]) {
        map[s.employee_id] = s.day;
      }
    });
    return map;
  })();

  const tasksFor = (empId, day) => tasks.filter(t => {
    if (t.employee_id !== empId) return false;
    if (t.day) return t.day === day;
    // Legacy monthly (no day): anchor to the employee's first shift of the month
    if (t.frequency === 'monthly') return firstShiftDay[empId] === day;
    return false;
  });
  const doneCount = tasks.filter(t => t.done).length;
  const empDndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  return (
    <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6 space-y-5">
      {/* HEADER */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl tracking-wide uppercase text-white mb-1">
            Team <span className="text-brand">Tasks & Daily Log</span>
          </h2>
          <p className="font-sans text-xs text-neutral-500">Build a master task list, upload the monthly schedule, then Auto-Assign to distribute tasks across staff.</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="bg-neutral-900 border border-neutral-800 rounded-xl p-1 flex items-center">
            <button onClick={() => setView('calendar')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-heading tracking-widest uppercase transition-colors ${view==='calendar' ? 'bg-brand text-white' : 'text-neutral-400 hover:text-white'}`}><CalIcon size={12} /> Calendar</button>
            <button onClick={() => setView('tasks')} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-heading tracking-widest uppercase transition-colors ${view==='tasks' ? 'bg-brand text-white' : 'text-neutral-400 hover:text-white'}`}><ListChecks size={12} /> Task Library</button>
          </div>
          <div className="flex items-center gap-2 bg-neutral-900 border border-neutral-800 rounded-xl p-1">
            <button onClick={prevMonth} data-testid="tasks-prev-month" className="p-2 hover:bg-neutral-800 rounded-lg text-neutral-300"><ChevronLeft size={16} /></button>
            <div className="font-heading text-sm tracking-widest uppercase text-white px-3 min-w-[160px] text-center">{monthLabel(cursor)}</div>
            <button onClick={nextMonth} data-testid="tasks-next-month" className="p-2 hover:bg-neutral-800 rounded-lg text-neutral-300"><ChevronRight size={16} /></button>
          </div>
        </div>
      </div>

      {/* STATS */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard icon={Users}        label="Employees" value={employees.length} />
        <StatCard icon={CalIcon}      label="Shifts" value={shifts.length} />
        <StatCard icon={Sunrise}      label="Walk-Ins" value={walkins.length} />
        <StatCard icon={CalendarDays} label="Reservations" value={reservations.reduce((sum, r) => sum + (r.count || 0), 0)} />
      </div>

      {/* PER-EMPLOYEE COMPLETION */}
      {employees.length > 0 && (
        <section className="bg-neutral-900/60 border border-neutral-800 rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-heading text-[11px] tracking-widest uppercase text-neutral-400 flex items-center gap-2">
              <Check size={14} className="text-brand" /> Completion – {monthLabel(cursor)}
            </h3>
            <span className="text-[11px] font-sans text-neutral-500">{doneCount} / {tasks.length} total</span>
          </div>
          <DndContext sensors={empDndSensors} collisionDetection={closestCenter} onDragEnd={(e) => {
            if (e.active.id !== e.over?.id) {
              const oldIdx = employees.findIndex(emp => emp.id === e.active.id);
              const newIdx = employees.findIndex(emp => emp.id === e.over.id);
              const next = arrayMove(employees, oldIdx, newIdx);
              setEmployees(next);
              axios.put(`${API}/api/team/employees/reorder`, { ids: next.map(em => em.id) }, { withCredentials: true });
            }
          }}>
            <SortableContext items={employees.map(e => e.id)} strategy={verticalListSortingStrategy}>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {employees.map(emp => {
                  const empTasks = tasks.filter(t => t.employee_id === emp.id);
                  const done = empTasks.filter(t => t.done).length;
                  const total = empTasks.length;
                  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                  const empWalkins = walkins.filter(w => w.employee_id === emp.id);
                  const empReservations = reservations.filter(r => r.employee_id === emp.id).reduce((sum, r) => sum + (r.count || 0), 0);
                  return (
                    <SortableEmployeeCard key={emp.id} emp={emp} done={done} total={total} pct={pct} empWalkins={empWalkins} empReservations={empReservations} />
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        </section>
      )}

      {/* ARCHIVE — past months with saved data */}
      {archive.length > 0 && (
        <section className="bg-neutral-900/60 border border-neutral-800 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-2">
            <CalIcon size={14} className="text-neutral-500" />
            <h3 className="font-heading text-[11px] tracking-widest uppercase text-neutral-400">Archive – past months</h3>
            <span className="text-[10px] font-sans text-neutral-600">auto-saved · click to reopen</span>
          </div>
          <div className="flex flex-wrap gap-1.5">
            {archive.map(a => {
              const [ay, am] = a.month.split('-').map(Number);
              const label = new Date(ay, am - 1, 1).toLocaleString('en-US', { month: 'short', year: '2-digit' });
              const isActive = a.month === month;
              return (
                <button
                  key={a.month}
                  onClick={() => setCursor(new Date(ay, am - 1, 1))}
                  className={`flex items-center gap-1 rounded-lg px-2.5 py-1.5 border transition-all ${isActive ? 'border-brand bg-brand/10 text-brand' : 'border-neutral-800 bg-black/40 hover:border-neutral-600 text-neutral-300'}`}
                  data-testid={`archive-${a.month}`}
                >
                  <span className="font-heading text-[11px] tracking-wide uppercase">{label}</span>
                  <span className="font-sans text-[9px] opacity-70">· {a.shifts}s / {a.tasks}t{a.has_image ? ' · 📷' : ''}</span>
                </button>
              );
            })}
          </div>
        </section>
      )}

      {notice && (
        <div className="bg-brand/10 border border-brand/30 rounded-xl px-4 py-3 text-brand font-sans text-sm flex items-center justify-between">
          {notice}
          <button onClick={() => setNotice('')} className="text-brand/70 hover:text-brand"><X size={14} /></button>
        </div>
      )}

      {exportError && (
        <div className="bg-amber-500/10 border border-amber-500/40 rounded-xl px-4 py-3 text-amber-400 font-sans text-sm flex items-center justify-between">
          <span>⚠ {exportError}</span>
          <button onClick={() => setExportError('')} className="text-amber-300/70 hover:text-amber-300"><X size={14} /></button>
        </div>
      )}

      {/* SCHEDULE UPLOAD */}
      <section className="bg-neutral-900/60 border border-neutral-800 rounded-xl overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-800 flex-wrap gap-2">
          <div className="flex items-center gap-2 font-heading text-[11px] tracking-widest uppercase text-neutral-300">
            <ImageIcon size={14} />
            Source Schedule Photo
          </div>
          <div className="flex items-center gap-2">
            {schedule && (
              <button onClick={() => runParse()} disabled={parsing} className="flex items-center gap-1.5 text-[11px] font-heading tracking-widest uppercase bg-neutral-800 text-white px-3 py-1.5 rounded-lg hover:bg-neutral-700 disabled:opacity-50">
                {parsing ? <><Loader2 size={12} className="animate-spin" /> Reading...</> : <><Sparkles size={12} /> Scan Again</>}
              </button>
            )}
            <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onFileChange} data-testid="schedule-file-input" />
            <button onClick={() => fileInputRef.current?.click()} data-testid="upload-schedule-btn" className="flex items-center gap-1.5 text-[11px] font-heading tracking-widest uppercase bg-brand text-white px-3 py-1.5 rounded-lg hover:bg-brand-hover transition-colors">
              <Upload size={12} /> {schedule ? 'Upload New Schedule' : 'Upload Schedule'}
            </button>
            {schedule && (
              <button onClick={removeSchedule} className="flex items-center gap-1 text-[11px] text-neutral-500 hover:text-brand"><X size={12} /> Remove</button>
            )}
          </div>
        </div>
        {parsing && (
          <div className="bg-black/40 py-3 px-4 border-b border-neutral-800 flex items-center gap-2 text-neutral-300 font-sans text-xs">
            <Loader2 size={14} className="animate-spin text-brand" /> AI is reading your schedule...
          </div>
        )}
        {parseError && <div className="bg-brand/10 border-b border-brand/30 text-brand font-sans text-xs px-4 py-2">{parseError}</div>}
        {parseInfo && <ParseSummary info={parseInfo} onClose={() => setParseInfo(null)} />}
        {schedule?.image_base64 && (
          <div className="p-4 bg-black/40">
            <img src={`data:image/png;base64,${schedule.image_base64}`} alt={`Schedule ${month}`} className="w-full rounded-lg border border-neutral-800" />
          </div>
        )}
        {!schedule && (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <ImageIcon size={32} className="text-neutral-700 mb-2" />
            <p className="font-sans text-xs text-neutral-500">No schedule uploaded. Upload a photo and AI fills the calendar automatically.</p>
          </div>
        )}
      </section>

      {/* AUTO-ASSIGN */}
      <section className="bg-neutral-900/60 border border-brand/40 rounded-xl p-4" style={{ boxShadow: '0 0 24px rgba(255, 26, 26, 0.08)' }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-heading text-sm tracking-wide uppercase text-white flex items-center gap-2"><Wand2 size={14} className="text-brand" /> Auto-Assign Library → Calendar</h3>
            <p className="font-sans text-xs text-neutral-500 mt-1">
              <strong className="text-neutral-300">Sync Missing</strong> adds only new library tasks (keeps your completions). <strong className="text-neutral-300">Auto-Assign</strong> wipes & re-distributes everything for {monthLabel(cursor)}.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={runRollover} disabled={shifts.length === 0 || tasks.length === 0} data-testid="rollover-btn" className="flex items-center gap-2 font-heading text-xs tracking-widest uppercase bg-neutral-800 text-white px-4 py-3 rounded-xl hover:bg-neutral-700 disabled:opacity-40 disabled:cursor-not-allowed" title="Move any overdue uncompleted weekly/monthly tasks to that employee's next shift">
              <CalendarRange size={14} /> Roll Over
            </button>
            <button onClick={runSyncLibrary} disabled={assigning || library.length === 0 || shifts.length === 0} data-testid="sync-library-btn" className="flex items-center gap-2 font-heading text-xs tracking-widest uppercase bg-emerald-600 text-white px-4 py-3 rounded-xl hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed" title="Add only missing library tasks for this month — completions stay intact">
              {assigning ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Sync Missing
            </button>
            <button onClick={runAutoAssign} disabled={assigning || library.length === 0 || shifts.length === 0} data-testid="auto-assign-btn" className="flex items-center gap-2 font-heading tracking-widest uppercase bg-brand text-white px-5 py-3 rounded-xl hover:bg-brand-hover disabled:opacity-40 disabled:cursor-not-allowed">
              {assigning ? <><Loader2 size={16} className="animate-spin" /> Assigning...</> : <><Wand2 size={16} /> Auto-Assign</>}
            </button>
          </div>
        </div>
        {(library.length === 0 || shifts.length === 0) && (
          <div className="mt-3 pt-3 border-t border-neutral-800 text-[11px] text-neutral-500 font-sans">
            {library.length === 0 && '• Add tasks to the library below before assigning. '}
            {shifts.length === 0 && '• Upload the monthly schedule so the AI can map tasks to shifts.'}
          </div>
        )}
      </section>

      {/* RYCOM MEETINGS (above calendar) */}
      {view === 'calendar' && (
        <RycomMeetings month={month} monthLabel={monthLabel(cursor)} employees={employees} daysInMonth={totalDays} meetings={meetings} setMeetings={setMeetings} />
      )}

      {/* CALENDAR VIEW */}
      {view === 'calendar' && (
        <section ref={calendarRef} className="bg-neutral-900/60 border border-neutral-800 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-neutral-800 flex items-center justify-between flex-wrap gap-2">
            <h3 className="font-heading text-[11px] tracking-widest uppercase text-neutral-400">Monthly Calendar – {monthLabel(cursor)}</h3>
            <div className="flex items-center gap-3 flex-wrap">
              <div className="flex flex-wrap items-center gap-2">
                {employees.map(e => (
                  <div key={e.id} className="flex items-center gap-1.5">
                    <div className="w-3 h-3 rounded-full" style={{ background: e.color }} />
                    <span className="text-[10px] font-sans text-neutral-300">{e.name}</span>
                  </div>
                ))}
              </div>
              <button
                onClick={downloadPdf}
                disabled={exporting || shifts.length === 0}
                data-testid="download-calendar-pdf"
                data-pdf-hide="true"
                className="flex items-center gap-1.5 text-[11px] font-heading tracking-widest uppercase bg-brand text-white px-3 py-1.5 rounded-lg hover:bg-brand-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                title="Download this month's calendar as a printable PDF"
              >
                {exporting ? <><Loader2 size={12} className="animate-spin" /> Preparing...</> : <><Printer size={12} /> Print PDF</>}
              </button>
            </div>
          </div>

          {shifts.length === 0 && !loading && (
            <div className="py-12 text-center">
              <CalIcon size={44} className="text-neutral-700 mx-auto mb-3" />
              <p className="font-sans text-sm text-neutral-500">No shifts yet for this month.</p>
              <p className="font-sans text-xs text-neutral-600 mt-1">Upload the schedule photo above.</p>
            </div>
          )}

          {shifts.length > 0 && (
            <div className="p-3 overflow-x-auto">
              <div className="grid grid-cols-7 gap-1.5 min-w-[720px]">
                {DOW.map((d, i) => (
                  <div key={i} className="font-heading text-[10px] tracking-widest uppercase text-neutral-500 text-center py-2">{d}</div>
                ))}
                {Array.from({ length: firstDow }).map((_, i) => <div key={`pad-${i}`} />)}
                {Array.from({ length: totalDays }).map((_, idx) => {
                  const day = idx + 1;
                  const dayShifts = shifts.filter(s => s.day === day);
                  const dayMeetings = meetings.filter(mt => mt.day === day);
                  const cellDate = new Date(y, m - 1, day);
                  const isWknd = cellDate.getDay() === 0 || cellDate.getDay() === 6;
                  const now = new Date();
                  const isToday = now.getFullYear() === y && (now.getMonth() + 1) === m && now.getDate() === day;
                  return (
                    <div
                      key={day}
                      className={`relative rounded-lg border p-2 min-h-[110px] flex flex-col transition-colors ${isToday ? 'border-brand/70' : 'border-neutral-800'} ${isWknd ? 'bg-black/60' : 'bg-black/30'}`}
                      style={isToday ? { background: 'linear-gradient(145deg, rgba(230,55,63,0.18), rgba(230,55,63,0.04))', boxShadow: '0 0 22px rgba(230,55,63,0.18), inset 0 0 0 1px rgba(230,55,63,0.35)' } : undefined}
                      data-testid={isToday ? 'calendar-today' : undefined}
                    >
                      <div className={`font-heading text-xs mb-1 flex items-center gap-1.5 ${isToday ? 'text-brand' : isWknd ? 'text-brand' : 'text-neutral-400'}`}>
                        <span>{day}</span>
                        {isToday && (
                          <span className="text-[8px] font-heading tracking-widest uppercase px-1 py-0.5 rounded bg-brand/20 text-brand border border-brand/40">Today</span>
                        )}
                        {dayMeetings.length > 0 && (
                          <span
                            className="ml-auto text-[14px] leading-none"
                            style={{ color: '#facc15', textShadow: '0 0 6px rgba(250,204,21,0.9), 0 0 12px rgba(250,204,21,0.5)' }}
                            title={dayMeetings.map(mt => {
                              const emp = employees.find(e => e.id === mt.employee_id);
                              return `Meeting${mt.time ? ' @ ' + mt.time : ''}: ${mt.title} → ${emp?.name || ''}`;
                            }).join('\n')}
                          >
                            ★
                          </span>
                        )}
                      </div>

                      {/* Neon-yellow meeting badges */}
                      {dayMeetings.map(mt => {
                        const emp = mt.employee_id ? employees.find(e => e.id === mt.employee_id) : null;
                        const attendeeName = emp?.name || mt.attendee_name || '';
                        return (
                          <div
                            key={mt.id}
                            className="mb-1 rounded px-1.5 py-1"
                            style={{
                              background: 'rgba(250, 204, 21, 0.12)',
                              border: '1px solid rgba(250, 204, 21, 0.55)',
                              boxShadow: '0 0 10px rgba(250, 204, 21, 0.25)',
                            }}
                            title={`${mt.title}${mt.time ? ' @ ' + mt.time : ''} — ${attendeeName}`}
                          >
                            <div className="flex items-center gap-1">
                              <span className="text-[10px]" style={{ color: '#facc15', textShadow: '0 0 4px rgba(250,204,21,0.8)' }}>★</span>
                              <span
                                className="text-[10px] font-heading font-bold tracking-wide uppercase truncate"
                                style={{ color: '#facc15', textShadow: '0 0 4px rgba(250,204,21,0.7)' }}
                              >
                                Meeting
                              </span>
                            </div>
                            {attendeeName && (
                              <div className="text-[9px] font-sans truncate mt-0.5" style={{ color: '#fde047' }}>
                                {attendeeName.split(' ')[0]}{mt.time ? ` · ${mt.time}` : ''}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      <div className="space-y-1.5 flex-1">
                        {dayShifts.map(s => {
                          const emp = employees.find(e => e.id === s.employee_id);
                          if (!emp) return null;
                          const firstName = emp.name.split(' ')[0];
                          const dayTasks = tasksFor(emp.id, day);
                          const doneTasks = dayTasks.filter(t => t.done).length;
                          return (
                            <button
                              key={s.id}
                              type="button"
                              onClick={() => setFocus({ employee: emp, day, shift: s })}
                              className="w-full rounded px-1.5 py-1 hover:brightness-125 transition-all text-left block"
                              style={{ background: `${emp.color}25`, borderLeft: `2px solid ${emp.color}` }}
                              title={`${emp.name} · ${s.start}–${s.end}${dayTasks.length ? `\n${dayTasks.map(t => (t.done ? '☑ ' : '☐ ') + t.title).join('\n')}` : ''}`}
                            >
                              <div className="flex items-center gap-1">
                                <span className="text-[10px] font-heading font-bold truncate" style={{ color: emp.color }}>{firstName}</span>
                                {dayTasks.length > 0 && (
                                  <span className="ml-auto text-[8px] font-heading font-bold px-1 rounded shrink-0" style={{ background: emp.color, color: '#000' }}>
                                    {doneTasks}/{dayTasks.length}
                                  </span>
                                )}
                              </div>
                              {dayTasks.length === 0 ? (
                                <div className="text-[9px] font-sans text-white/50 truncate">{s.start}–{s.end}</div>
                              ) : (
                                <div className="space-y-0.5 mt-0.5">
                                  {dayTasks.slice(0, 3).map(t => (
                                    <div key={t.id} className={`text-[9px] font-sans truncate leading-tight ${t.done ? 'line-through opacity-60' : ''}`} style={{ color: '#fff' }}>
                                      • {t.title}
                                    </div>
                                  ))}
                                  {dayTasks.length > 3 && (
                                    <div className="text-[8px] font-sans italic" style={{ color: emp.color }}>+{dayTasks.length - 3} more</div>
                                  )}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                      <AddShiftInline
                        day={day}
                        employees={employees}
                        onAdd={addShift}
                        emptyState={dayShifts.length === 0}
                        onShiftAdded={(newShift, emp) => setFocus({ employee: emp, day, shift: newShift })}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {/* TEAM ACTIVITY LOG */}
      {view === 'calendar' && (
        <section className="bg-neutral-900/60 border border-neutral-800 rounded-xl p-4" data-testid="team-chat-section">
          <div className="flex items-center gap-2 mb-3">
            <MessageSquare size={14} className="text-brand" />
            <h3 className="font-heading text-[11px] tracking-widest uppercase text-neutral-400">Activity Log — {monthLabel(cursor)}</h3>
          </div>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const msg = chatDraft.trim();
              if (!msg) return;
              try {
                const { data } = await axios.post(`${API}/api/team/chat`, { message: msg, month, author_name: chatAuthor.trim() || undefined }, { withCredentials: true });
                setChatMessages(prev => [data, ...prev]);
                setChatDraft('');
              } catch {}
            }}
            className="flex gap-2 mb-3"
          >
            <input
              value={chatDraft}
              onChange={e => setChatDraft(e.target.value)}
              placeholder="Type a note..."
              data-testid="chat-input"
              className="flex-1 bg-black border border-neutral-800 rounded-xl px-4 py-3 text-white font-sans text-sm focus:border-brand focus:outline-none placeholder-neutral-600"
            />
            <input
              value={chatAuthor}
              onChange={e => setChatAuthor(e.target.value)}
              placeholder="Name"
              data-testid="chat-author-input"
              className="w-28 sm:w-36 bg-black border border-neutral-800 rounded-xl px-3 py-3 text-white font-sans text-sm focus:border-brand focus:outline-none placeholder-neutral-600"
            />
            <button
              type="submit"
              disabled={!chatDraft.trim()}
              data-testid="chat-send-btn"
              className="flex items-center gap-1.5 font-heading text-xs tracking-widest uppercase bg-brand text-white px-4 py-3 rounded-xl hover:bg-brand-hover transition-colors disabled:opacity-30"
            >
              <Send size={14} /> Post
            </button>
          </form>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {chatMessages.length === 0 && (
              <p className="font-sans text-xs text-neutral-600 italic text-center py-4">No notes yet this month.</p>
            )}
            {chatMessages.map(msg => (
              <div key={msg.id} className="flex gap-3 group" data-testid={`chat-msg-${msg.id}`}>
                <div className="w-1 rounded-full bg-brand/40 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-sans text-sm text-white leading-relaxed">{msg.message}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-sans text-[10px] text-neutral-600">{msg.author}</span>
                    <span className="font-sans text-[10px] text-neutral-700">·</span>
                    <span className="font-sans text-[10px] text-neutral-600">
                      {new Date(msg.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} at {new Date(msg.created_at).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                    </span>
                  </div>
                </div>
                <button
                  onClick={async () => {
                    await axios.delete(`${API}/api/team/chat/${msg.id}`, { withCredentials: true });
                    setChatMessages(prev => prev.filter(m => m.id !== msg.id));
                  }}
                  className="text-neutral-700 hover:text-red-400 transition-colors p-1 shrink-0"
                  title="Delete"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* SUPPLIES NEEDED */}
      {view === 'calendar' && (
        <section className="bg-neutral-900/60 border border-neutral-800 rounded-xl p-4" data-testid="supplies-section">
          <div className="flex items-center gap-2 mb-3">
            <ListChecks size={14} className="text-yellow-500" />
            <h3 className="font-heading text-[11px] tracking-widest uppercase text-neutral-400">Supplies Needed</h3>
            {supplies.filter(s => !s.done).length > 0 && (
              <span className="font-heading text-[10px] tracking-widest uppercase bg-yellow-500/20 text-yellow-500 px-2 py-0.5 rounded-full">{supplies.filter(s => !s.done).length} pending</span>
            )}
          </div>
          <form
            onSubmit={async (e) => {
              e.preventDefault();
              const item = supplyDraft.trim();
              if (!item) return;
              try {
                const { data } = await axios.post(`${API}/api/team/supplies`, { item, added_by: supplyAuthor.trim() || undefined }, { withCredentials: true });
                setSupplies(prev => [data, ...prev]);
                setSupplyDraft('');
              } catch {}
            }}
            className="flex gap-2 mb-3"
          >
            <input
              value={supplyDraft}
              onChange={e => setSupplyDraft(e.target.value)}
              placeholder="e.g. Toilet paper almost out"
              data-testid="supply-input"
              className="flex-1 bg-black border border-neutral-800 rounded-xl px-4 py-3 text-white font-sans text-sm focus:border-yellow-500 focus:outline-none placeholder-neutral-600"
            />
            <input
              value={supplyAuthor}
              onChange={e => setSupplyAuthor(e.target.value)}
              placeholder="Name"
              data-testid="supply-author-input"
              className="w-28 sm:w-36 bg-black border border-neutral-800 rounded-xl px-3 py-3 text-white font-sans text-sm focus:border-yellow-500 focus:outline-none placeholder-neutral-600"
            />
            <button
              type="submit"
              disabled={!supplyDraft.trim()}
              data-testid="supply-add-btn"
              className="flex items-center gap-1.5 font-heading text-xs tracking-widest uppercase bg-yellow-600 text-black px-4 py-3 rounded-xl hover:bg-yellow-500 transition-colors disabled:opacity-30"
            >
              <Plus size={14} /> Add
            </button>
          </form>
          <div className="space-y-1.5 max-h-60 overflow-y-auto">
            {supplies.length === 0 && (
              <p className="font-sans text-xs text-neutral-600 italic text-center py-3">No supplies needed right now.</p>
            )}
            {supplies.map(s => (
              <div key={s.id} className="flex items-center gap-3" data-testid={`supply-item-${s.id}`}>
                <button
                  onClick={async () => {
                    const { data } = await axios.put(`${API}/api/team/supplies/${s.id}`, {}, { withCredentials: true });
                    setSupplies(prev => prev.map(x => x.id === s.id ? { ...x, done: data.done } : x));
                  }}
                  data-testid={`supply-check-${s.id}`}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${s.done ? 'bg-yellow-500 border-yellow-500' : 'border-neutral-600 hover:border-yellow-500'}`}
                >
                  {s.done && <Check size={12} className="text-black" />}
                </button>
                <div className="flex-1 min-w-0">
                  <span className={`font-sans text-sm ${s.done ? 'line-through text-neutral-600' : 'text-white'}`}>{s.item}</span>
                  {s.added_by && <span className="font-sans text-[10px] text-neutral-600 ml-2">— {s.added_by}</span>}
                </div>
                <button
                  onClick={async () => {
                    await axios.delete(`${API}/api/team/supplies/${s.id}`, { withCredentials: true });
                    setSupplies(prev => prev.filter(x => x.id !== s.id));
                  }}
                  className="text-neutral-700 hover:text-red-400 transition-colors p-1 shrink-0"
                >
                  <Trash2 size={12} />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* TASK LIBRARY VIEW */}
      {view === 'tasks' && (
        <div className="space-y-5">
          <LibrarySection frequency="daily"   library={library} onAdd={addLibraryTask} onUpdate={updateLibraryTask} onRemove={removeLibraryTask} />
          <LibrarySection frequency="weekly"  library={library} onAdd={addLibraryTask} onUpdate={updateLibraryTask} onRemove={removeLibraryTask} />
          <LibrarySection frequency="monthly" library={library} onAdd={addLibraryTask} onUpdate={updateLibraryTask} onRemove={removeLibraryTask} />

          {/* Employees panel */}
          <section className="bg-neutral-900/60 border border-neutral-800 rounded-xl p-4">
            <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
              <h3 className="font-heading text-[11px] tracking-widest uppercase text-neutral-400 flex items-center gap-2"><Users size={14} /> Employees</h3>
              <div className="flex gap-2 items-center">
                <input value={newEmployee} onChange={e => setNewEmployee(e.target.value)} onKeyDown={e => e.key === 'Enter' && addEmployee()} placeholder="Add employee..." data-testid="new-employee-input" className="bg-black border border-neutral-800 rounded-lg px-3 py-2 text-white font-sans text-xs focus:border-brand focus:outline-none" />
                <button onClick={addEmployee} data-testid="add-employee-btn" className="flex items-center gap-1 font-heading text-[11px] tracking-widest uppercase bg-brand text-white px-3 py-2 rounded-lg hover:bg-brand-hover"><Plus size={12} /> Add</button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {employees.map(emp => (
                <EmployeeRow
                  key={emp.id}
                  emp={emp}
                  editEmp={editEmp}
                  setEditEmp={setEditEmp}
                  saveEmployeeEdit={saveEmployeeEdit}
                  removeEmployee={removeEmployee}
                  shiftCount={shifts.filter(s => s.employee_id === emp.id).length}
                />
              ))}
            </div>
          </section>
        </div>
      )}

      {/* FOCUS MODAL */}
      {focus && (
        <FocusPanel
          focus={focus}
          onClose={() => setFocus(null)}
          tasks={tasksFor(focus.employee.id, focus.day)}
          allShiftsForEmployee={shifts.filter(s => s.employee_id === focus.employee.id)}
          onToggle={toggleTask}
          onUpdate={updateTaskTitle}
          onRemove={removeTask}
          onPushNext={pushTaskToNextShift}
          onAdd={(title) => addTaskForEmployee(focus.employee.id, title, focus.day)}
          onSelectShift={(s) => setFocus({ employee: focus.employee, day: s.day, shift: s })}
          walkins={walkins.filter(w => w.shift_id === focus.shift.id || (w.employee_id === focus.employee.id && w.day === focus.day && !w.shift_id))}
          onAddWalkin={(name, paid) => addWalkin(focus.employee.id, focus.day, focus.shift.id, name, paid)}
          onToggleWalkinPaid={toggleWalkinPaid}
          onUpdateWalkinName={updateWalkinName}
          onRemoveWalkin={removeWalkin}
          rooms={rooms}
          reservations={reservations.filter(r => r.shift_id === focus.shift.id || (r.employee_id === focus.employee.id && r.day === focus.day && !r.shift_id))}
          onAddRoom={addRoom}
          onRemoveRoom={removeRoom}
          onSetReservation={(roomId, count) => setReservationCount(focus.employee.id, focus.day, focus.shift.id, roomId, count)}
          onReassign={async (newEmpId) => {
            const newEmp = employees.find(e => e.id === newEmpId);
            const updated = await reassignShift(focus.shift.id, newEmpId);
            if (newEmp) setFocus({ employee: newEmp, day: updated.day, shift: updated });
          }}
          onDeleteShift={async () => {
            if (!window.confirm('Remove this shift from the schedule?')) return;
            await deleteShift(focus.shift.id);
            setFocus(null);
          }}
          employees={employees}
          month={month}
          monthDisplay={monthLabel(cursor)}
          routineItems={routineItems}
          routineCompletions={routineCompletions}
          onAddRoutineItem={addRoutineItem}
          onRemoveRoutineItem={removeRoutineItem}
          onReorderRoutineItem={reorderRoutineItem}
          onToggleRoutineCompletion={toggleRoutineCompletion}
          onFetchRoutineCompletions={fetchRoutineCompletions}
          closingRoutineItems={closingRoutineItems}
          onAddClosingRoutineItem={addClosingRoutineItem}
          onRemoveClosingRoutineItem={removeClosingRoutineItem}
          onReorderClosingRoutineItem={reorderClosingRoutineItem}
        />
      )}
    </div>
  );
}

// -------- Library section (Daily / Weekly / Monthly) --------
function LibrarySection({ frequency, library, onAdd, onUpdate, onRemove }) {
  const meta = FREQ_META[frequency];
  const Icon = meta.icon;
  const [draft, setDraft] = useState('');
  const items = library.filter(t => t.frequency === frequency);

  const submit = async (e) => {
    e?.preventDefault?.();
    const v = draft.trim();
    if (!v) return;
    await onAdd(v, frequency);
    setDraft('');
  };

  return (
    <section className="bg-neutral-900/60 border border-neutral-800 rounded-xl overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-4 py-3 border-b border-neutral-800">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-brand/10 border border-brand/30 flex items-center justify-center">
            <Icon size={14} className="text-brand" />
          </div>
          <div>
            <h3 className="font-heading text-sm tracking-wide uppercase text-white">{meta.label} Tasks</h3>
            <p className="font-sans text-[11px] text-neutral-500">{meta.desc}</p>
          </div>
        </div>
        <span className="text-[11px] font-sans text-neutral-500">{items.length} task{items.length !== 1 ? 's' : ''}</span>
      </div>

      <div className="p-4 space-y-2">
        {items.length === 0 && (
          <p className="font-sans text-xs text-neutral-600 italic text-center py-3">No {meta.label.toLowerCase()} tasks yet — add the first one below.</p>
        )}
        {items.map(t => (
          <LibraryRow key={t.id} item={t} onUpdate={onUpdate} onRemove={onRemove} />
        ))}
        <form onSubmit={submit} className="flex gap-2 pt-2 border-t border-neutral-800 mt-2">
          <input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            placeholder={`New ${meta.label.toLowerCase()} task (e.g. ${frequency === 'daily' ? 'Wipe down front desk' : frequency === 'weekly' ? 'Restock prize drawer' : 'Deep clean escape rooms'})`}
            data-testid={`lib-input-${frequency}`}
            className="flex-1 bg-black border border-neutral-800 rounded-lg px-3 py-2 text-white font-sans text-xs focus:border-brand focus:outline-none"
          />
          <button type="submit" data-testid={`lib-add-${frequency}`} className="flex items-center gap-1 font-heading text-[11px] tracking-widest uppercase bg-brand text-white px-3 py-2 rounded-lg hover:bg-brand-hover">
            <Plus size={12} /> Add
          </button>
        </form>
      </div>
    </section>
  );
}

function LibraryRow({ item, onUpdate, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(item.title);
  const save = () => {
    const v = value.trim();
    if (v && v !== item.title) onUpdate(item.id, { title: v });
    setEditing(false);
  };
  return (
    <div className="flex items-center gap-2 bg-black/40 border border-neutral-800 rounded-lg px-3 py-2 group hover:border-neutral-700 transition-colors">
      <div className="w-1.5 h-1.5 rounded-full bg-brand shrink-0" />
      {editing ? (
        <input autoFocus value={value} onChange={e => setValue(e.target.value)} onBlur={save}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setValue(item.title); setEditing(false); } }}
          className="flex-1 bg-black border border-neutral-700 rounded px-2 py-1 text-white font-sans text-sm focus:border-brand focus:outline-none" />
      ) : (
        <p onClick={() => setEditing(true)} className="flex-1 font-sans text-sm text-white cursor-pointer">{item.title}</p>
      )}
      <button onClick={() => onRemove(item.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-neutral-500 hover:text-brand p-1"><Trash2 size={14} /></button>
    </div>
  );
}

function TaskRow({ task, color, onToggle, onUpdate, onRemove, onPushNext }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(task.title);
  const [pushing, setPushing] = useState(false);
  const save = () => {
    const v = value.trim();
    if (v && v !== task.title) onUpdate(task, v);
    setEditing(false);
  };
  const freqBadge = {
    daily: { label: 'D', color: '#22d3ee' },
    weekly: { label: 'W', color: '#ffe15a' },
    monthly: { label: 'M', color: '#ff2a9d' },
  }[task.frequency];
  const handlePush = async () => {
    if (pushing || !onPushNext) return;
    setPushing(true);
    try { await onPushNext(task); }
    finally { setPushing(false); }
  };
  // Only show push button for non-daily tasks that aren't already done
  const canPush = onPushNext && task.frequency && task.frequency !== 'daily' && !task.done;
  return (
    <div className="flex items-center gap-3 group bg-black/40 border border-neutral-800 rounded-lg px-3 py-2 hover:border-neutral-700 transition-colors">
      <button onClick={() => onToggle(task)} className="shrink-0 w-5 h-5 rounded flex items-center justify-center transition-all" style={{ border: `1.5px solid ${color}`, background: task.done ? color : 'transparent' }}>
        {task.done && <Check size={12} style={{ color: '#000' }} strokeWidth={3} />}
      </button>
      {editing ? (
        <input autoFocus value={value} onChange={e => setValue(e.target.value)} onBlur={save}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setValue(task.title); setEditing(false); } }}
          className="flex-1 bg-black border border-neutral-700 rounded px-2 py-1 text-white font-sans text-sm focus:border-brand focus:outline-none" />
      ) : (
        <p onClick={() => setEditing(true)} className={`flex-1 font-sans text-sm cursor-pointer ${task.done ? 'line-through text-neutral-500' : 'text-white'}`}>{task.title}</p>
      )}
      {freqBadge && (
        <span className="text-[9px] font-heading font-bold px-1.5 py-0.5 rounded" style={{ background: `${freqBadge.color}25`, color: freqBadge.color, border: `1px solid ${freqBadge.color}55` }}>
          {freqBadge.label}
        </span>
      )}
      {canPush && (
        <button
          onClick={handlePush}
          disabled={pushing}
          title="Move to my next shift"
          data-testid={`push-next-${task.id}`}
          className="opacity-70 group-hover:opacity-100 hover:text-brand text-neutral-400 p-1 transition-colors disabled:opacity-30"
        >
          {pushing ? <Loader2 size={14} className="animate-spin" /> : <CalendarPlus size={14} />}
        </button>
      )}
      <button onClick={() => onRemove(task.id)} className="opacity-0 group-hover:opacity-100 transition-opacity text-neutral-500 hover:text-brand p-1"><Trash2 size={14} /></button>
    </div>
  );
}

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="bg-neutral-900/60 border border-neutral-800 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} className="text-brand" />
        <span className="font-heading text-[10px] tracking-widest uppercase text-neutral-500">{label}</span>
      </div>
      <p className="font-heading text-2xl text-white">{value}</p>
    </div>
  );
}

function EmployeeRow({ emp, editEmp, setEditEmp, saveEmployeeEdit, removeEmployee, shiftCount }) {
  const [confirming, setConfirming] = useState(false);
  useEffect(() => {
    if (!confirming) return;
    const t = setTimeout(() => setConfirming(false), 3000);
    return () => clearTimeout(t);
  }, [confirming]);

  const isEditing = editEmp?.id === emp.id;
  const initials = emp.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div className="flex items-center gap-2 bg-black/40 border border-neutral-800 rounded-lg p-2" data-testid={`emp-row-${emp.id}`}>
      <div className="w-8 h-8 rounded-full flex items-center justify-center font-heading text-xs font-bold shrink-0" style={{ background: emp.color, color: '#000' }}>
        {initials}
      </div>
      {isEditing ? (
        <>
          <input value={editEmp.name} onChange={e => setEditEmp({ ...editEmp, name: e.target.value })} className="flex-1 min-w-0 bg-black border border-neutral-800 rounded px-2 py-1 text-white font-sans text-xs focus:border-brand focus:outline-none" />
          <input type="color" value={editEmp.color} onChange={e => setEditEmp({ ...editEmp, color: e.target.value })} className="w-7 h-7 shrink-0 rounded bg-transparent border border-neutral-800 cursor-pointer" />
          <button onClick={saveEmployeeEdit} className="shrink-0 p-1.5 text-brand hover:bg-brand/10 rounded" title="Save"><Check size={14} /></button>
          <button onClick={() => setEditEmp(null)} className="shrink-0 p-1.5 text-neutral-400 hover:bg-neutral-800 rounded" title="Cancel"><X size={14} /></button>
        </>
      ) : (
        <>
          <div className="flex-1 min-w-0">
            <p className="font-sans text-xs text-white truncate">{emp.name}</p>
            <p className="font-sans text-[10px] text-neutral-500">{shiftCount} shift{shiftCount !== 1 ? 's' : ''}</p>
          </div>
          <button
            onClick={() => setEditEmp(emp)}
            className="shrink-0 p-1.5 text-neutral-400 hover:text-brand hover:bg-neutral-800 rounded"
            title="Edit"
            data-testid={`edit-emp-${emp.id}`}
          >
            <Edit3 size={14} />
          </button>
          {confirming ? (
            <button
              onClick={() => removeEmployee(emp.id)}
              className="shrink-0 flex items-center gap-1 px-2 py-1 rounded bg-brand text-white font-heading text-[10px] tracking-widest uppercase hover:bg-brand-hover"
              data-testid={`confirm-delete-emp-${emp.id}`}
            >
              <Trash2 size={12} /> Confirm
            </button>
          ) : (
            <button
              onClick={() => setConfirming(true)}
              className="shrink-0 p-1.5 text-neutral-400 hover:text-brand hover:bg-neutral-800 rounded"
              title="Delete"
              data-testid={`delete-emp-${emp.id}`}
            >
              <Trash2 size={14} />
            </button>
          )}
        </>
      )}
    </div>
  );
}

function FocusPanel({ focus, onClose, tasks, allShiftsForEmployee, onToggle, onUpdate, onRemove, onPushNext, onAdd, onSelectShift, onReassign, onDeleteShift, employees, walkins = [], onAddWalkin, onToggleWalkinPaid, onUpdateWalkinName, onRemoveWalkin, rooms = [], reservations = [], onAddRoom, onRemoveRoom, onSetReservation, month, monthDisplay, routineItems = [], routineCompletions = [], onAddRoutineItem, onRemoveRoutineItem, onReorderRoutineItem, onToggleRoutineCompletion, onFetchRoutineCompletions, closingRoutineItems = [], onAddClosingRoutineItem, onRemoveClosingRoutineItem, onReorderClosingRoutineItem }) {
  const { employee: emp, day, shift } = focus;
  const [draft, setDraft] = useState('');
  const [reassigning, setReassigning] = useState(false);
  const [reassignQuery, setReassignQuery] = useState('');
  const [routineDraft, setRoutineDraft] = useState('');
  const [closingDraft, setClosingDraft] = useState('');
  const done = tasks.filter(t => t.done).length;

  useEffect(() => {
    if (shift?.id) onFetchRoutineCompletions(shift.id);
  }, [shift?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    const v = draft.trim();
    if (!v) return;
    await onAdd(v);
    setDraft('');
  };

  // Group tasks by frequency
  const groups = { daily: [], weekly: [], monthly: [], other: [] };
  tasks.forEach(t => {
    const k = t.frequency || 'other';
    (groups[k] || groups.other).push(t);
  });

  const dndSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  return (
    <div className="fixed inset-0 z-[100] flex items-stretch justify-end" data-testid="focus-panel">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:w-[460px] bg-neutral-950 border-l border-neutral-800 shadow-2xl overflow-y-auto" style={{ boxShadow: `-24px 0 60px ${emp.color}22` }}>
        <div className="p-5 border-b border-neutral-800 relative" style={{ background: `linear-gradient(135deg, ${emp.color}25, transparent 70%)` }}>
          <button onClick={onClose} className="absolute top-4 right-4 text-neutral-500 hover:text-white p-1"><X size={18} /></button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full flex items-center justify-center font-heading text-lg font-bold shrink-0" style={{ background: emp.color, color: '#000' }}>
              {emp.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="font-heading text-lg tracking-wide text-white truncate">{emp.name}</p>
              <p className="font-sans text-xs text-neutral-400">{monthDisplay}, Day {day}</p>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <div className="inline-flex items-center gap-2 rounded-lg px-3 py-1.5" style={{ background: 'rgba(0,0,0,0.5)', border: `1px solid ${emp.color}60` }}>
              <CalIcon size={13} style={{ color: emp.color }} />
              <span className="font-heading text-xs tracking-wide uppercase" style={{ color: emp.color }}>Shift: {shift.start || '—'} – {shift.end || '—'}</span>
            </div>
            <button
              type="button"
              onClick={() => { setReassigning(v => !v); setReassignQuery(''); }}
              data-testid="reassign-toggle"
              className="inline-flex items-center gap-1 text-[10px] font-heading tracking-widest uppercase bg-black/50 border border-neutral-700 text-neutral-300 hover:text-white hover:border-brand px-2.5 py-1.5 rounded-lg transition-colors"
              title="Someone else is covering this shift? Reassign it."
            >
              <Edit3 size={11} /> Reassign
            </button>
            <button
              type="button"
              onClick={onDeleteShift}
              data-testid="delete-shift-btn"
              className="inline-flex items-center gap-1 text-[10px] font-heading tracking-widest uppercase bg-black/50 border border-neutral-800 text-neutral-500 hover:text-red-400 hover:border-red-500/60 px-2.5 py-1.5 rounded-lg transition-colors"
              title="Remove this shift"
            >
              <Trash2 size={11} />
            </button>
          </div>

          {/* Mini revenue strip — PIN gated */}
          <DailyRevenueStrip month={month} day={day} />

          {reassigning && (
            <div className="mt-3 p-3 rounded-lg border border-neutral-800 bg-black/60 space-y-2" data-testid="reassign-panel">
              <p className="text-[10px] font-heading tracking-widest uppercase text-neutral-400">Reassign this shift to:</p>
              <input
                autoFocus
                value={reassignQuery}
                onChange={e => setReassignQuery(e.target.value)}
                placeholder="Type a name..."
                data-testid="reassign-input"
                className="w-full bg-black border border-neutral-800 rounded-lg px-3 py-2 text-white font-sans text-xs focus:border-brand focus:outline-none"
              />
              <div className="flex flex-wrap gap-1.5 pt-1">
                {employees
                  .filter(e => e.id !== emp.id)
                  .filter(e => !reassignQuery || e.name.toLowerCase().includes(reassignQuery.toLowerCase()))
                  .map(e => (
                    <button
                      key={e.id}
                      type="button"
                      onClick={async () => { await onReassign(e.id); setReassigning(false); setReassignQuery(''); }}
                      data-testid={`reassign-to-${e.id}`}
                      className="text-[11px] font-sans rounded-full px-2.5 py-1 hover:scale-105 transition-transform"
                      style={{ background: `${e.color}25`, border: `1px solid ${e.color}80`, color: e.color }}
                    >
                      {e.name}
                    </button>
                  ))}
                {employees.filter(e => e.id !== emp.id).filter(e => !reassignQuery || e.name.toLowerCase().includes(reassignQuery.toLowerCase())).length === 0 && (
                  <p className="text-[10px] font-sans italic text-neutral-500">No matching employees. Add them in the Task Library tab first.</p>
                )}
              </div>
              <p className="text-[9px] font-sans text-neutral-500 italic pt-1">Tasks for this day will transfer to the new person.</p>
            </div>
          )}
        </div>

        <div className="p-5 space-y-4">
          {/* Open Routine */}
          {(() => {
            const openDone = routineItems.length > 0 && routineCompletions.filter(c => c.shift_id === shift.id && c.done && routineItems.some(ri => ri.id === c.routine_id)).length >= routineItems.length;
            return (
          <div data-testid="open-routine-section" className={`border rounded-xl p-4 transition-colors duration-300 ${openDone ? 'border-green-500/50 bg-green-900/10' : 'border-yellow-700/40 bg-yellow-900/10'}`}>
            <div className="flex items-center justify-between mb-3">
              <h4 className={`font-heading text-[11px] tracking-widest uppercase transition-colors ${openDone ? 'text-green-400' : 'text-yellow-500'}`}>Open Routine {openDone ? '✓' : ''}</h4>
              <span className="text-[11px] font-sans text-neutral-500">
                {routineItems.length > 0 ? `${routineCompletions.filter(c => c.shift_id === shift.id && c.done).length}/${routineItems.length} done` : ''}
              </span>
            </div>
            {routineItems.length === 0 ? (
              <p className="font-sans text-xs text-neutral-600 italic text-center py-2">No routine items yet — add one below.</p>
            ) : (
              <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={(e) => { if (e.active.id !== e.over?.id) { const oi = routineItems.findIndex(r => r.id === e.active.id); const ni = routineItems.findIndex(r => r.id === e.over.id); onReorderRoutineItem(oi, ni); } }}>
                <SortableContext items={routineItems.map(r => r.id)} strategy={verticalListSortingStrategy}>
                  <div className="space-y-1.5 mb-3">
                    {routineItems.map((item, idx) => {
                      const isDone = routineCompletions.some(c => c.routine_id === item.id && c.shift_id === shift.id && c.done);
                      return (
                        <SortableRoutineItem key={item.id} id={item.id} idx={idx} title={item.title} isDone={isDone} allDone={openDone} color="yellow" onToggle={() => onToggleRoutineCompletion(item.id, shift.id, !isDone)} onRemove={() => onRemoveRoutineItem(item.id)} />
                      );
                    })}
                  </div>
                </SortableContext>
              </DndContext>
            )}
            <form onSubmit={(e) => { e.preventDefault(); const v = routineDraft.trim(); if (!v) return; onAddRoutineItem(v); setRoutineDraft(''); }} className="flex gap-2">
              <input
                value={routineDraft}
                onChange={e => setRoutineDraft(e.target.value)}
                placeholder="Add routine step..."
                data-testid="routine-add-input"
                className="flex-1 bg-black border border-neutral-800 rounded-lg px-3 py-2 text-white font-sans text-xs focus:border-yellow-500 focus:outline-none placeholder-neutral-600"
              />
              <button type="submit" disabled={!routineDraft.trim()} data-testid="routine-add-btn" className="font-heading text-[10px] tracking-widest uppercase text-yellow-500 border border-yellow-700/60 px-3 py-2 rounded-lg hover:bg-yellow-900/30 transition-colors disabled:opacity-30">+ Add</button>
            </form>
            <p className="font-sans text-[9px] text-neutral-600 mt-2 italic">Permanent steps that carry over every month. Staff check these off each shift.</p>
          </div>
            );
          })()}

          <div>
            <div className="flex items-center justify-between mb-2">
              <h4 className="font-heading text-[11px] tracking-widest uppercase text-neutral-400">Tasks</h4>
              <span className="text-[11px] font-sans text-neutral-500">{done}/{tasks.length} done</span>
            </div>

            {tasks.length === 0 ? (
              <p className="font-sans text-xs text-neutral-600 italic bg-black/40 border border-neutral-800 rounded-lg px-3 py-3 text-center">
                No tasks for this shift yet.
              </p>
            ) : (
              <div className="space-y-3">
                {['daily', 'weekly', 'monthly', 'other'].map(freq => {
                  const list = groups[freq];
                  if (!list.length) return null;
                  const label = freq === 'other' ? 'Manual' : FREQ_META[freq]?.label || freq;
                  return (
                    <div key={freq}>
                      <p className="text-[10px] font-heading tracking-widest uppercase text-neutral-500 mb-1.5">{label}</p>
                      <div className="space-y-2">
                        {list.map(t => (
                          <TaskRow key={t.id} task={t} color={emp.color} onToggle={onToggle} onUpdate={onUpdate} onRemove={onRemove} onPushNext={onPushNext} />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <form onSubmit={submit} className="flex gap-2 pt-3 border-t border-neutral-800">
            <input autoFocus value={draft} onChange={e => setDraft(e.target.value)} placeholder="Add a task to this shift..." data-testid="focus-new-task" className="flex-1 bg-black border border-neutral-800 rounded-lg px-3 py-2 text-white font-sans text-xs focus:border-brand focus:outline-none" />
            <button type="submit" className="flex items-center gap-1 font-heading text-[11px] tracking-widest uppercase bg-brand text-white px-3 py-2 rounded-lg hover:bg-brand-hover">
              <Plus size={12} /> Add
            </button>
          </form>

          <WalkInsSection
            color={emp.color}
            walkins={walkins}
            onAdd={onAddWalkin}
            onTogglePaid={onToggleWalkinPaid}
            onUpdateName={onUpdateWalkinName}
            onRemove={onRemoveWalkin}
          />

          <ReservationsSection
            color={emp.color}
            rooms={rooms}
            reservations={reservations}
            month={month}
            day={day}
            onAddRoom={onAddRoom}
            onRemoveRoom={onRemoveRoom}
            onSetReservation={onSetReservation}
          />

          {/* Closing Routine */}
          <div data-testid="closing-routine-section" className="pt-4 border-t border-neutral-800">
            {(() => {
              const closeDone = closingRoutineItems.length > 0 && routineCompletions.filter(c => c.shift_id === shift.id && c.done && closingRoutineItems.some(ci => ci.id === c.routine_id)).length >= closingRoutineItems.length;
              return (
            <div className={`border rounded-xl p-4 transition-colors duration-300 ${closeDone ? 'border-green-500/50 bg-green-900/10' : 'border-blue-700/40 bg-blue-900/10'}`}>
              <div className="flex items-center justify-between mb-3">
                <h4 className={`font-heading text-[11px] tracking-widest uppercase transition-colors ${closeDone ? 'text-green-400' : 'text-blue-400'}`}>Closing Routine {closeDone ? '✓' : ''}</h4>
                <span className="text-[11px] font-sans text-neutral-500">
                  {closingRoutineItems.length > 0 ? `${routineCompletions.filter(c => c.shift_id === shift.id && c.done && closingRoutineItems.some(ci => ci.id === c.routine_id)).length}/${closingRoutineItems.length} done` : ''}
                </span>
              </div>
              {closingRoutineItems.length === 0 ? (
                <p className="font-sans text-xs text-neutral-600 italic text-center py-2">No closing routine items yet — add one below.</p>
              ) : (
                <DndContext sensors={dndSensors} collisionDetection={closestCenter} onDragEnd={(e) => { if (e.active.id !== e.over?.id) { const oi = closingRoutineItems.findIndex(r => r.id === e.active.id); const ni = closingRoutineItems.findIndex(r => r.id === e.over.id); onReorderClosingRoutineItem(oi, ni); } }}>
                  <SortableContext items={closingRoutineItems.map(r => r.id)} strategy={verticalListSortingStrategy}>
                    <div className="space-y-1.5 mb-3">
                      {closingRoutineItems.map((item, idx) => {
                        const isDone = routineCompletions.some(c => c.routine_id === item.id && c.shift_id === shift.id && c.done);
                        return (
                          <SortableRoutineItem key={item.id} id={item.id} idx={idx} title={item.title} isDone={isDone} allDone={closeDone} color="blue" onToggle={() => onToggleRoutineCompletion(item.id, shift.id, !isDone)} onRemove={() => onRemoveClosingRoutineItem(item.id)} />
                        );
                      })}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
              <form onSubmit={(e) => { e.preventDefault(); const v = closingDraft.trim(); if (!v) return; onAddClosingRoutineItem(v); setClosingDraft(''); }} className="flex gap-2">
                <input
                  value={closingDraft}
                  onChange={e => setClosingDraft(e.target.value)}
                  placeholder="Add closing step..."
                  data-testid="closing-add-input"
                  className="flex-1 bg-black border border-neutral-800 rounded-lg px-3 py-2 text-white font-sans text-xs focus:border-blue-500 focus:outline-none placeholder-neutral-600"
                />
                <button type="submit" disabled={!closingDraft.trim()} data-testid="closing-add-btn" className="font-heading text-[10px] tracking-widest uppercase text-blue-400 border border-blue-700/60 px-3 py-2 rounded-lg hover:bg-blue-900/30 transition-colors disabled:opacity-30">+ Add</button>
              </form>
              <p className="font-sans text-[9px] text-neutral-600 mt-2 italic">Permanent closing steps. Staff check these off at the end of each shift.</p>
            </div>
              );
            })()}
          </div>
        </div>
      </div>
    </div>
  );
}


function AddShiftInline({ day, employees, onAdd, emptyState = false, onShiftAdded }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  if (employees.length === 0) return null;
  const handlePick = async (e) => {
    const newShift = await onAdd(e.id, day);
    setOpen(false);
    setQuery('');
    if (onShiftAdded && newShift) onShiftAdded(newShift, e);
  };
  if (!open) {
    if (emptyState) {
      return (
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid={`add-shift-day-${day}`}
          className="flex-1 mt-1 w-full flex flex-col items-center justify-center gap-1 rounded border border-dashed border-neutral-800 text-neutral-600 hover:text-brand hover:border-brand/60 hover:bg-brand/5 transition-colors group min-h-[60px]"
          title="Click to add a shift and log day info (open/close checklist, reservations, walk-ins)"
        >
          <Plus size={14} className="opacity-50 group-hover:opacity-100 transition-opacity" />
          <span className="text-[9px] font-heading tracking-widest uppercase">+ Day Notes</span>
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid={`add-shift-day-${day}`}
        className="mt-1 w-full text-[9px] font-heading tracking-widest uppercase text-neutral-600 hover:text-brand hover:border-brand border border-dashed border-neutral-800 rounded px-1 py-0.5 transition-colors"
        title="Add a coverage shift for this day"
      >
        + shift
      </button>
    );
  }
  const matches = employees.filter(e => !query || e.name.toLowerCase().includes(query.toLowerCase()));
  return (
    <div className="mt-1 p-1.5 rounded border border-neutral-700 bg-black/80 space-y-1" onClick={e => e.stopPropagation()}>
      {emptyState && (
        <p className="text-[9px] font-heading tracking-widest uppercase text-neutral-500 mb-0.5">Who's covering today?</p>
      )}
      <input
        autoFocus
        value={query}
        onChange={e => setQuery(e.target.value)}
        placeholder="Name..."
        data-testid={`add-shift-input-${day}`}
        className="w-full bg-black border border-neutral-800 rounded px-1.5 py-0.5 text-white font-sans text-[10px] focus:border-brand focus:outline-none"
      />
      <div className="flex flex-wrap gap-1 max-h-24 overflow-y-auto">
        {matches.map(e => (
          <button
            key={e.id}
            type="button"
            onClick={() => handlePick(e)}
            data-testid={`add-shift-pick-${day}-${e.id}`}
            className="text-[9px] font-sans rounded px-1.5 py-0.5 hover:brightness-125"
            style={{ background: `${e.color}25`, border: `1px solid ${e.color}80`, color: e.color }}
          >
            {e.name.split(' ')[0]}
          </button>
        ))}
        {matches.length === 0 && (
          <span className="text-[9px] italic text-neutral-500">No match</span>
        )}
      </div>
      <button
        type="button"
        onClick={() => { setOpen(false); setQuery(''); }}
        className="text-[9px] font-sans text-neutral-500 hover:text-white"
      >
        cancel
      </button>
    </div>
  );
}

function WalkInsSection({ color, walkins, onAdd, onTogglePaid, onUpdateName, onRemove }) {
  const [name, setName] = useState('');
  const [paid, setPaid] = useState(false);
  const paidCount = walkins.filter(w => w.paid).length;
  const submit = async (e) => {
    e.preventDefault();
    const v = name.trim();
    if (!v) return;
    await onAdd(v, paid);
    setName('');
    setPaid(false);
  };
  return (
    <div className="pt-4 border-t border-neutral-800" data-testid="walkins-section">
      <div className="flex items-center justify-between mb-2">
        <h4 className="font-heading text-sm tracking-widest uppercase text-white">Walk-In Customers</h4>
        <span className="text-xs font-sans text-neutral-400">{paidCount}/{walkins.length} paid</span>
      </div>

      <p className="text-xs font-sans italic text-amber-300/80 bg-amber-500/5 border border-amber-500/30 rounded-lg px-3 py-2 mb-3 leading-snug">
        Note: if the customer does not pay at the time of walk-in, the walk-in does not count towards bonus.
      </p>

      {walkins.length > 0 && (
        <div className="space-y-2 mb-3">
          {walkins.map(w => (
            <WalkInRow key={w.id} w={w} color={color} onTogglePaid={onTogglePaid} onUpdateName={onUpdateName} onRemove={onRemove} />
          ))}
        </div>
      )}

      <form onSubmit={submit} className="flex gap-2 items-center">
        <label className="flex items-center gap-1.5 cursor-pointer select-none" title="Paid at walk-in?">
          <input
            type="checkbox"
            checked={paid}
            onChange={e => setPaid(e.target.checked)}
            data-testid="new-walkin-paid"
            className="accent-brand w-4 h-4"
          />
          <span className="text-xs font-heading tracking-widest uppercase text-neutral-300">Paid</span>
        </label>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Walk-in name..."
          data-testid="new-walkin-name"
          className="flex-1 bg-black border border-neutral-800 rounded-lg px-3 py-2 text-white font-sans text-sm focus:border-brand focus:outline-none"
        />
        <button type="submit" data-testid="add-walkin-btn" className="flex items-center gap-1 font-heading text-xs tracking-widest uppercase bg-brand text-white px-3 py-2 rounded-lg hover:bg-brand-hover">
          <Plus size={14} /> Add
        </button>
      </form>
    </div>
  );
}

function WalkInRow({ w, color, onTogglePaid, onUpdateName, onRemove }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(w.name);
  const save = async () => {
    const v = draft.trim();
    if (v && v !== w.name) await onUpdateName(w, v);
    setEditing(false);
  };
  return (
    <div
      className="flex items-center gap-2 rounded-lg px-3 py-2"
      style={{
        background: w.paid ? `${color}12` : 'rgba(255,255,255,0.02)',
        border: `1px solid ${w.paid ? `${color}50` : '#262626'}`,
      }}
      data-testid={`walkin-${w.id}`}
    >
      <label className="flex items-center cursor-pointer" title={w.paid ? 'Paid at walk-in' : 'Unpaid — does NOT count toward bonus'}>
        <input
          type="checkbox"
          checked={w.paid}
          onChange={() => onTogglePaid(w)}
          data-testid={`walkin-paid-${w.id}`}
          className="w-4 h-4"
          style={{ accentColor: color }}
        />
      </label>
      {editing ? (
        <input
          autoFocus
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onBlur={save}
          onKeyDown={e => { if (e.key === 'Enter') save(); if (e.key === 'Escape') { setDraft(w.name); setEditing(false); } }}
          className="flex-1 bg-black border border-neutral-800 rounded px-2 py-1 text-white font-sans text-sm focus:border-brand focus:outline-none"
        />
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          className={`flex-1 text-left font-sans text-sm truncate ${w.paid ? 'text-white' : 'text-neutral-300 italic'}`}
          title="Click to rename"
        >
          {w.name}
        </button>
      )}
      {!w.paid && (
        <span className="text-[10px] font-heading tracking-widest uppercase text-amber-400/90 shrink-0">Unpaid</span>
      )}
      <button
        type="button"
        onClick={() => onRemove(w.id)}
        className="text-neutral-600 hover:text-red-400 p-0.5"
        title="Remove walk-in"
        data-testid={`walkin-remove-${w.id}`}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}


function ReservationsSection({ color, rooms, reservations, onAddRoom, onRemoveRoom, onSetReservation, month, day }) {
  const [adding, setAdding] = useState(false);
  const [newRoom, setNewRoom] = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState(null);
  const total = reservations.reduce((sum, r) => sum + (r.count || 0), 0);

  const countFor = (roomId) => {
    const r = reservations.find(x => x.room_id === roomId);
    return r ? r.count : 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = newRoom.trim();
    if (!v) return;
    await onAddRoom(v);
    setNewRoom('');
    setAdding(false);
  };

  const syncFromSquare = async () => {
    if (!month || !day) return;
    setSyncing(true);
    setSyncMsg(null);
    try {
      const { data } = await axios.get(`${API}/api/admin/square/bookings?month=${month}`, { withCredentials: true });
      // For each Square service group, try to match a local room by case-insensitive name overlap
      let matched = 0;
      let unmatched = [];
      for (const [svcName, days] of Object.entries(data.grouped || {})) {
        const count = days[day] || 0;
        if (!count) continue;
        const lower = svcName.toLowerCase();
        const room = rooms.find(r => {
          const rn = r.name.toLowerCase();
          return rn.includes(lower) || lower.includes(rn);
        });
        if (room) {
          await onSetReservation(room.id, count);
          matched++;
        } else {
          unmatched.push(`${svcName}: ${count}`);
        }
      }
      setSyncMsg({
        ok: true,
        text: matched > 0
          ? `Synced ${matched} room${matched !== 1 ? 's' : ''} from Square${unmatched.length ? ` · No match: ${unmatched.join(', ')}` : ''}`
          : (unmatched.length ? `No matches. Square has: ${unmatched.join(', ')}` : 'No bookings for this day'),
      });
    } catch (err) {
      setSyncMsg({ ok: false, text: err.response?.data?.detail || 'Sync failed — add a Square account first' });
    }
    setSyncing(false);
    setTimeout(() => setSyncMsg(null), 6000);
  };

  return (
    <div className="pt-4 border-t border-neutral-800" data-testid="reservations-section">
      <div className="flex items-center justify-between mb-2 gap-2">
        <h4 className="font-heading text-sm tracking-widest uppercase text-white">Reservations Completed</h4>
        <div className="flex items-center gap-2">
          <span className="text-xs font-sans text-neutral-400">{total} total</span>
          <button
            type="button"
            onClick={syncFromSquare}
            disabled={syncing || rooms.length === 0}
            data-testid="sync-square-bookings-btn"
            title="Auto-fill counts from Square bookings for this day"
            className="flex items-center gap-1 text-[10px] font-heading tracking-widest uppercase border border-border text-muted-foreground hover:text-brand hover:border-brand/60 px-2 py-1 rounded transition-colors disabled:opacity-40"
          >
            {syncing ? <Loader2 size={10} className="animate-spin" /> : <RefreshCw size={10} />}
            Sync Square
          </button>
        </div>
      </div>

      <p className="text-xs font-sans text-neutral-500 mb-3">Enter how many reservations were completed this shift, per room.</p>

      {syncMsg && (
        <div className={`text-[10px] font-sans rounded px-2 py-1.5 mb-2 ${syncMsg.ok ? 'bg-green-500/10 text-green-400 border border-green-500/30' : 'bg-red-500/10 text-red-400 border border-red-500/30'}`} data-testid="sync-square-msg">
          {syncMsg.text}
        </div>
      )}

      <div className="space-y-2">
        {rooms.map(room => (
          <ReservationRow
            key={room.id}
            room={room}
            color={color}
            value={countFor(room.id)}
            onChange={(n) => onSetReservation(room.id, n)}
            onRemoveRoom={() => {
              if (window.confirm(`Remove room "${room.name}" from tracking? All reservations tied to it will also be cleared.`)) {
                onRemoveRoom(room.id);
              }
            }}
          />
        ))}
        {rooms.length === 0 && (
          <p className="text-xs font-sans italic text-neutral-600 text-center py-2">No rooms yet. Add one below.</p>
        )}
      </div>

      {adding ? (
        <form onSubmit={submit} className="flex gap-2 items-center mt-3">
          <input
            autoFocus
            value={newRoom}
            onChange={e => setNewRoom(e.target.value)}
            placeholder="New room name..."
            data-testid="new-room-input"
            className="flex-1 bg-black border border-neutral-800 rounded-lg px-3 py-2 text-white font-sans text-sm focus:border-brand focus:outline-none"
          />
          <button type="submit" data-testid="add-room-btn" className="flex items-center gap-1 font-heading text-xs tracking-widest uppercase bg-brand text-white px-3 py-2 rounded-lg hover:bg-brand-hover">
            <Plus size={14} /> Add
          </button>
          <button type="button" onClick={() => { setAdding(false); setNewRoom(''); }} className="text-xs font-sans text-neutral-500 hover:text-white px-2">
            Cancel
          </button>
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          data-testid="add-room-toggle"
          className="mt-3 w-full flex items-center justify-center gap-1 font-heading text-[11px] tracking-widest uppercase text-neutral-400 hover:text-brand border border-dashed border-neutral-800 hover:border-brand rounded-lg px-3 py-2 transition-colors"
        >
          <Plus size={12} /> Add Room
        </button>
      )}
    </div>
  );
}

function ReservationRow({ room, color, value, onChange, onRemoveRoom }) {
  const [local, setLocal] = useState(String(value || 0));
  useEffect(() => { setLocal(String(value || 0)); }, [value]);

  const commit = async (n) => {
    const clean = Math.max(0, parseInt(n, 10) || 0);
    if (clean === (value || 0)) return;
    await onChange(clean);
  };

  return (
    <div
      className="flex items-center gap-2 rounded-lg px-3 py-2"
      style={{
        background: value > 0 ? `${color}12` : 'rgba(255,255,255,0.02)',
        border: `1px solid ${value > 0 ? `${color}50` : '#262626'}`,
      }}
      data-testid={`reservation-row-${room.id}`}
    >
      <p className="flex-1 font-sans text-sm text-white truncate" title={room.name}>{room.name}</p>
      <button
        type="button"
        onClick={() => commit(Math.max(0, (parseInt(local, 10) || 0) - 1))}
        className="w-7 h-7 rounded-md border border-neutral-700 text-neutral-300 hover:border-brand hover:text-brand font-heading text-sm leading-none"
        data-testid={`reservation-dec-${room.id}`}
        aria-label={`Decrease ${room.name}`}
      >
        −
      </button>
      <input
        type="number"
        min="0"
        value={local}
        onChange={e => setLocal(e.target.value)}
        onBlur={() => commit(local)}
        onKeyDown={e => { if (e.key === 'Enter') { e.currentTarget.blur(); } }}
        className="w-14 bg-black border border-neutral-800 rounded-lg px-2 py-1 text-white font-heading text-sm text-center focus:border-brand focus:outline-none"
        style={value > 0 ? { color } : {}}
        data-testid={`reservation-count-${room.id}`}
      />
      <button
        type="button"
        onClick={() => commit((parseInt(local, 10) || 0) + 1)}
        className="w-7 h-7 rounded-md border border-neutral-700 text-neutral-300 hover:border-brand hover:text-brand font-heading text-sm leading-none"
        data-testid={`reservation-inc-${room.id}`}
        aria-label={`Increase ${room.name}`}
      >
        +
      </button>
      <button
        type="button"
        onClick={onRemoveRoom}
        className="text-neutral-600 hover:text-red-400 p-1"
        title="Remove this room from tracking"
        data-testid={`reservation-remove-${room.id}`}
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}


function SortableRoutineItem({ id, idx, title, isDone, allDone, color, onToggle, onRemove }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const checkClass = allDone
    ? 'bg-green-500 border-green-500'
    : isDone
      ? (color === 'blue' ? 'bg-blue-500 border-blue-500' : 'bg-yellow-500 border-yellow-500')
      : (color === 'blue' ? 'border-neutral-600 hover:border-blue-500' : 'border-neutral-600 hover:border-yellow-500');

  return (
    <div ref={setNodeRef} style={style} className="flex items-center gap-2" data-testid={`routine-item-${id}`}>
      <button {...attributes} {...listeners} className="text-neutral-600 hover:text-neutral-400 transition-colors p-0.5 touch-none" title="Drag to reorder">
        <GripVertical size={14} />
      </button>
      <span className="font-heading text-xs text-neutral-600 w-5 text-right shrink-0">{idx + 1}.</span>
      <button onClick={onToggle} data-testid={`routine-check-${id}`} className={`w-5 h-5 rounded border-2 flex items-center justify-center shrink-0 transition-colors ${checkClass}`}>
        {isDone && <Check size={12} className="text-black" />}
      </button>
      <span className={`font-sans text-sm flex-1 ${isDone ? 'line-through text-neutral-600' : 'text-white'}`}>{title}</span>
      <button onClick={onRemove} className="text-neutral-600 hover:text-red-400 transition-all p-1" title="Remove">
        <Trash2 size={12} />
      </button>
    </div>
  );
}


function SortableEmployeeCard({ emp, done, total, pct, empWalkins, empReservations }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: emp.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const initials = emp.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();

  return (
    <div ref={setNodeRef} style={style} className="bg-black/40 border border-neutral-800 rounded-lg p-3" data-testid={`emp-card-${emp.id}`}>
      <div className="flex items-center gap-3 mb-2">
        <div {...attributes} {...listeners} className="touch-none cursor-grab active:cursor-grabbing shrink-0">
          <GripVertical size={14} className="text-neutral-600" />
        </div>
        <div className="w-8 h-8 rounded-full flex items-center justify-center font-heading text-xs font-bold shrink-0" style={{ background: emp.color, color: '#000' }}>
          {initials}
        </div>
        <div className="min-w-0 flex-1">
          <p className="font-sans text-xs text-white truncate">{emp.name}</p>
          <p className="font-sans text-[10px] text-neutral-500">{total === 0 ? 'No tasks assigned' : `${done} of ${total} completed · ${pct}%`}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="font-heading text-lg leading-none" style={{ color: emp.color }}>{done}</p>
          <p className="font-sans text-[9px] text-neutral-500">/ {total}</p>
        </div>
      </div>
      <div className="h-1.5 bg-neutral-800 rounded-full overflow-hidden">
        <div className="h-full transition-all" style={{ width: `${pct}%`, background: `linear-gradient(90deg, ${emp.color}, ${emp.color}cc)`, boxShadow: pct > 0 ? `0 0 8px ${emp.color}88` : 'none' }} />
      </div>
      <div className="mt-3 pt-2 border-t border-neutral-800/80 flex items-center justify-between gap-2">
        <p className="font-heading text-[10px] tracking-widest uppercase text-neutral-500">Walk-Ins</p>
        <div className="flex items-baseline gap-1.5">
          <span className="font-heading text-base leading-none" style={{ color: emp.color }}>{empWalkins.length}</span>
          <span className="text-[10px] font-sans text-neutral-500">total</span>
        </div>
      </div>
      <div className="mt-2 flex items-center justify-between gap-2">
        <p className="font-heading text-[10px] tracking-widest uppercase text-neutral-500">Reservations</p>
        <div className="flex items-baseline gap-1.5">
          <span className="font-heading text-base leading-none" style={{ color: emp.color }}>{empReservations}</span>
          <span className="text-[10px] font-sans text-neutral-500">total</span>
        </div>
      </div>
    </div>
  );
}

